// Invoice Requests queue — ZYNX-CRM-SPEC §4.2.
//
// One screen serving both sides of the bridge:
//   • Accounts and the Superadmin see the whole queue and give their signature
//     when it is their turn.
//   • Sales sees its own requests, tracks exactly who a document is waiting on,
//     and moves it forward (submit, request proforma, resubmit).
//
// Every button here is rendered from server-computed flags on each row. The
// server re-checks role and sequence on every call, so this file decides what is
// *offered*, never what is *permitted* — an out-of-turn approval returns 409 and
// an unauthorized one 403, whatever the UI shows.

import { hasRealProformaNumber, isProformaApproved, isQuotationUsable, printedLineNote } from '@jira-clone/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/shared/hooks/useAuth';
import { useCompanyProfile } from '@/features/recruitment/api';
import { useDocumentMasterData } from '@/shared/hooks/useDocumentMasterData';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FileText, Send, Lock, Check, X, Trash2, RefreshCw, Receipt, Eye, Edit, Layers, Loader2, CheckSquare, ArrowLeft, ChevronDown } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { generateFullQuotationHtml, generateFullProformaInvoiceHtml, generateFullTaxInvoiceHtml } from '@/utils/sales/quotationTemplateTokens';
import { applyFinalInvoice } from '@/utils/sales/finalInvoiceDocument';
import { preparedByOf } from '@/utils/sales/preparedBy';
import { ProformaInvoiceGenerator } from '@/components/sales/ProformaInvoiceGenerator';
import { AccountingSubNav } from '@/components/sales/AccountingSubNav';
import { SendDocumentEmailModal } from '@/components/sales/SendDocumentEmailModal';
import {
  SalesDocumentWorkspaceDrawer,
  type DocumentWorkspaceData,
} from '@/components/sales/SalesDocumentWorkspaceDrawer';
import {
  useInvoiceRequests,
  useInvoiceRequestActions,
  APPROVER_LABELS,
  STATUS_LABELS,
  type ApprovalStage,
  type InvoiceRequest,
  type InvoiceRequestStatus,
  type WaitingOn,
} from '@/hooks/sales/useInvoiceRequests';
import { salesProformaEditPath } from '@/routes/paths';
import { cn, cardClassName } from '@jira-clone/ui';

const STATUS_TONE: Record<InvoiceRequestStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  requested: 'bg-blue-100 text-blue-800 border-blue-200',
  under_review: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  proforma_requested: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  proforma_under_review: 'bg-amber-100 text-amber-800 border-amber-200',
  proforma_approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  proforma_rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  invoice_pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  revision_needed: 'bg-orange-100 text-orange-800 border-orange-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  sent: 'bg-slate-200 text-slate-800 border-slate-300',
  confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700',
};

const FILTERS: Array<{ value: InvoiceRequestStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'proforma_requested', label: 'Awaiting Approval' },
  { value: 'proforma_approved', label: 'Approved' },
  { value: 'revision_needed', label: 'Revision Needed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'sent', label: 'Invoiced' },
];

function errorText(err: unknown, fallback: string): string {
  const e = err as any;
  const payload = e?.response?.data?.error ?? e?.response?.data?.message ?? e?.response?.data ?? e?.message;
  if (typeof payload === 'object' && payload !== null) {
    return payload.message || payload.error || payload.code || JSON.stringify(payload);
  }
  return typeof payload === 'string' && payload.trim() ? payload : fallback;
}

function inferMilestoneIndex(document: any): number | null {
  const token = String(document?.invoiceNumber || document?.proformaNumber || document?.number || '').match(/\(([IVXLCDM]+|\d+)\)/i)?.[1];
  if (!token) return null;
  const romanValues: Record<string, number> = { I: 0, II: 1, III: 2, IV: 3, V: 4, VI: 5, VII: 6, VIII: 7, IX: 8, X: 9 };
  const upper = token.toUpperCase();
  if (upper in romanValues) return romanValues[upper];
  const numeric = Number(token);
  return Number.isInteger(numeric) && numeric > 0 ? numeric - 1 : null;
}

/** Which stage a row is currently being decided at, if any. */
function stageOf(row: InvoiceRequest): ApprovalStage | null {
  if (row.stage) return row.stage;
  if (row.status === 'proforma_requested' || row.status === 'proforma_under_review') return 'proforma';
  return null;
}


/** A running bill (DT/RB/…): the bill for one stage of a milestone proforma. Never listed against the proforma. */
const isRunningBillRef = (x: any): boolean =>
  Boolean(x?.isRunningBill) || /(^|[-/])RB[-/]\d{4}/i.test(String((typeof x === 'string' ? x : (x?.invoiceNumber || x?.number)) || ''));

export function InvoiceRequestsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<InvoiceRequestStatus | ''>('');
  const [preparedByFilter, setPreparedByFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sendEmailDoc, setSendEmailDoc] = useState<InvoiceRequest | null>(null);
  // key = `inv-${r.id}-${idx}` → the newly-generated invoice request doc (from API response)
  const [generatedInvoices, setGeneratedInvoices] = useState<Record<string, any>>({});
  /** Row id currently collecting a rejection reason, plus the stage it applies to. */
  const [rejecting, setRejecting] = useState<{ id: string; stage: ApprovalStage } | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocData, setSelectedDocData] = useState<DocumentWorkspaceData | null>(null);
  const [createProformaQuotId, setCreateProformaQuotId] = useState<string | null>(null);
  const [proformaModalState, setProformaModalState] = useState<{
    open: boolean;
    quotationId?: string;
    quotationNumber?: string;
    milestoneIndex?: number;
    milestonePhaseLabel?: string;
    editingDraft?: any;
    isResubmission?: boolean;
    isRevision?: boolean;
    currentRevisionLabel?: string;
    nextRevisionLabel?: string;
    documentId?: string;
    rejectionReason?: string;
    proformaNumber?: string;
    fromDeal?: any;
    fromLead?: any;
  } | null>(null);

  const currentUser = useUser();
  const { data: companyProfile } = useCompanyProfile();
  const { data: masterData } = useDocumentMasterData();
  const defaultBankDetail = (masterData?.bankDetails ?? []).find((b) => b.isDefault && b.isActive);
  const userRole = (currentUser?.role || '').toLowerCase();
  const userDept = (currentUser?.department || '').toLowerCase();
  const isSuperAdmin = userRole === 'superadmin' || userRole === 'owner' || userRole === 'admin' || userDept === 'management' || userDept === 'superadmin';
  const isSalesUser = userDept === 'sales' || userRole === 'sales' || userRole === 'sales_executive' || userRole === 'sales_admin' || userRole === 'bde' || userRole === 'bda';
  const isSalesOnly = isSalesUser && !isSuperAdmin && userDept !== 'accounts';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isNewQuot = params.get('new') === 'quotation' || params.get('createQuotation') === 'true';
    if (isNewQuot) {
      navigate(`/quotations${location.search}`, { replace: true, state: location.state });
      return;
    }

    // Open a specific document when navigated from a notification (?documentId=<id>)
    const documentId = params.get('documentId');
    if (documentId) {
      api.get(`/api/invoice-requests/${documentId}`).then((res) => {
        const doc = res.data?.request || res.data?.doc || res.data?.data || res.data;
        if (doc) {
          setProformaModalState({
            open: true,
            editingDraft: doc,
            documentId: doc._id || doc.id || documentId,
            quotationNumber: doc.quotationNumber || doc.number || '',
          });
        }
      }).catch(() => {
        // Silently fail — the user can manually open from the list
      });
      return;
    }

    const hasCreate = params.get('createProforma') === 'true' || params.get('newProforma') === 'true' || params.get('new') === 'proforma' || params.get('requestProforma') === 'true' || Boolean((location.state as any)?.openProforma) || Boolean((location.state as any)?.fromDeal) || Boolean(params.get('dealId'));
    if (hasCreate) {
      const qId = params.get('quotationId') || (location.state as any)?.initialQuotationId || '';
      const qNo = params.get('quotationNumber') || (location.state as any)?.initialQuotationNumber || '';
      const mIdxParam = params.get('milestoneIndex') ?? (location.state as any)?.initialMilestoneIndex;
      const mIdx = mIdxParam !== undefined && mIdxParam !== null && mIdxParam !== '' ? Number(mIdxParam) : undefined;
      const phaseLabel = params.get('milestonePhaseLabel') || (location.state as any)?.initialMilestonePhaseLabel || '';
      const stateDeal = (location.state as any)?.fromDeal;
      const dealId = stateDeal?.dealId || stateDeal?.id || params.get('dealId');

      if (stateDeal) {
        setProformaModalState({
          open: true,
          quotationId: qId,
          quotationNumber: qNo,
          milestoneIndex: mIdx,
          milestonePhaseLabel: phaseLabel,
          fromDeal: stateDeal,
        });
      } else if (dealId) {
        api.get(`/api/deals/${dealId}`).then((res) => {
          const d = res.data?.deal || res.data?.data || res.data;
          setProformaModalState({
            open: true,
            quotationId: qId,
            quotationNumber: qNo,
            milestoneIndex: mIdx,
            milestonePhaseLabel: phaseLabel,
            fromDeal: d,
          });
        }).catch(() => {
          setProformaModalState({
            open: true,
            quotationId: qId,
            quotationNumber: qNo,
            milestoneIndex: mIdx,
            milestonePhaseLabel: phaseLabel,
          });
        });
      } else {
        setProformaModalState({
          open: true,
          quotationId: qId,
          quotationNumber: qNo,
          milestoneIndex: mIdx,
          milestonePhaseLabel: phaseLabel,
        });
      }
    }
  }, [location.search, location.state, navigate]);

  const { data, isLoading, isError, refetch } = useInvoiceRequests(filter);
  const actions = useInvoiceRequestActions();

  const handleOpenDrawer = (
    r: InvoiceRequest,
    docType: 'quotation' | 'proforma' | 'invoice' = 'quotation',
    milestoneIdx?: number,
    explicitDocNo?: string
  ) => {
    try {
      let targetR = r;
      const rawList = data?.requests || [];
      if (docType === 'quotation') {
        const quotCandidate = (r as any)._quotDoc
          || (r as any)._groupRows?.find((x: any) => x.requestType === 'quotation' || (x.number && /^QT/i.test(x.number)))
          || rawList.find((x: any) => (x.requestType === 'quotation' || (x.number && /^QT/i.test(x.number))) && (
            (r.sourceQuotationNumber && x.number === r.sourceQuotationNumber) ||
            (r.sourceQuotationId && (String(x._id || x.id) === String(r.sourceQuotationId))) ||
            (r.number && /^QT/i.test(r.number) && x.number === r.number)
          ));
        targetR = quotCandidate || r;
      } else if (docType === 'proforma') {
        const proformaCandidate = (r as any)._proformaDoc
          || (r.requestType === 'proforma' || (r.number && /^PI/i.test(r.number)) ? r : null)
          || (r as any)._groupRows?.find((x: any) => x.requestType === 'proforma' || (x.number && /^PI/i.test(x.number)))
          || rawList.find((x: any) => (x.requestType === 'proforma' || (x.number && /^PI/i.test(x.number))) && (
            (x.sourceQuotationNumber && (x.sourceQuotationNumber === r.number || x.sourceQuotationNumber === r.sourceQuotationNumber)) ||
            (x.sourceQuotationId && (String(x.sourceQuotationId) === String((r as any)._id || r.id || r.sourceQuotationId))) ||
            ((r as any).proformaId && String(x._id || x.id) === String((r as any).proformaId)) ||
            (r.proformaNumber && (x.number === r.proformaNumber || x.proformaNumber === r.proformaNumber))
          ));
        targetR = proformaCandidate || r;
      } else {
        targetR = (r as any)._invoiceDoc
          || (r as any)._groupRows?.find((x: any) => x.requestType === 'invoice' || (x.number && /^(?:TI|INV)/i.test(x.number)))
          || r;
      }

      const baseQuotNumber = (
        targetR.number ||
        r.sourceQuotationNumber ||
        (r as any)._quotDoc?.number ||
        r.number ||
        'QT/2026-27/001'
      );

      let htmlToShow = '';
      const coLogo = (targetR as any).template_data?.businessLogo || (targetR as any).template_data?.companyLogo || (targetR as any).businessLogo || (targetR as any).companyLogo || '';
      const coSig = (targetR as any).template_data?.signatureUrl || (targetR as any).signatureUrl || (targetR as any)._quotDoc?.template_data?.signatureUrl || (targetR as any)._quotDoc?.signatureUrl || '';
      // Bank details: the document's own saved values, else the organization's default Bank Detail master-data record
      const coBankAccName = (targetR as any).bankAccName || (targetR as any).template_data?.bankAccName || (r as any).bankAccName || defaultBankDetail?.accountName || '';
      const coBankAccNo = (targetR as any).bankAccNo || (targetR as any).template_data?.bankAccNo || (r as any).bankAccNo || defaultBankDetail?.accountNumber || '';
      const coBankName = (targetR as any).bankName || (targetR as any).template_data?.bankName || (r as any).bankName || defaultBankDetail?.bankName || '';
      // The saved document does not store the company's state; it is read from the live company profile.
      const coState: string = (companyProfile as any)?.registeredAddress?.state || '';
      const coBankBranch = (targetR as any).bankBranch || (targetR as any).template_data?.bankBranch || (r as any).bankBranch || defaultBankDetail?.branch || '';
      const coBankIfsc = (targetR as any).bankIfsc || (targetR as any).template_data?.bankIfsc || (r as any).bankIfsc || defaultBankDetail?.ifsc || '';
      const coBankUpi = (targetR as any).bankUpi || (targetR as any).template_data?.bankUpi || (r as any).bankUpi || defaultBankDetail?.upi || '';

      const finalDocForInvoice: any = docType === 'invoice' ? ((targetR as any).isFinalInvoice ? targetR : ((r as any).isFinalInvoice ? r : null)) : null;

      if (finalDocForInvoice) {
        // The final tax invoice: the whole contract plus the running bills it adjusts.
        htmlToShow = generateFullTaxInvoiceHtml(applyFinalInvoice({
          companyLogo: coLogo,
          companyState: coState,
          signatureUrl: coSig,
          bankAccName: coBankAccName,
          bankAccNo: coBankAccNo,
          bankName: coBankName,
          bankBranch: coBankBranch,
          bankIfsc: coBankIfsc,
          bankUpi: coBankUpi,
        } as any, finalDocForInvoice, {
          company: companyProfile as any,
          defaultDeclaration: (masterData?.declarations ?? []).find((d: any) => d.isDefault && d.isActive !== false)?.content,
        }));
      } else if (docType === 'invoice') {
        let activeInvoiceNo = explicitDocNo || (
          (targetR.invoiceNumber && !/^QT/i.test(targetR.invoiceNumber) && !/^PI/i.test(targetR.invoiceNumber) ? targetR.invoiceNumber : undefined) ||
          (targetR.number && /^(?:TI|INV)/i.test(targetR.number) ? targetR.number : undefined) ||
          (r.invoiceNumber && !/^QT/i.test(r.invoiceNumber) && !/^PI/i.test(r.invoiceNumber) ? r.invoiceNumber : undefined) ||
          (r.number && /^(?:TI|INV)/i.test(r.number) ? r.number : undefined) ||
          (targetR.number ? targetR.number.replace(/^(?:QTN?|PI)/i, 'TI') : undefined) ||
          (r.number ? r.number.replace(/^(?:QTN?|PI)/i, 'TI') : undefined) ||
          'TI-2026-27/001'
        );
        activeInvoiceNo = activeInvoiceNo.replace(/\([IVXLCDM\d]+\)$/i, '');
        const storedInvoiceHtml = (targetR as any).currentHtmlBody || (targetR as any).invoiceHtmlBody || '';
        const isRealTaxHtml = storedInvoiceHtml && (
          (storedInvoiceHtml.includes('data-doc="tax"') || storedInvoiceHtml.includes('>Tax Invoice</h1>') || storedInvoiceHtml.includes('>Tax Invoice<') || storedInvoiceHtml.includes('Payment Schedule &amp; Tax Division') || storedInvoiceHtml.includes('Payment Schedule & Tax Division')) &&
          !storedInvoiceHtml.includes('data-doc="proforma"') &&
          !storedInvoiceHtml.includes('data-doc="quotation"') &&
          !storedInvoiceHtml.includes('>Proforma Invoice</h1>') &&
          !storedInvoiceHtml.includes('Not a demand for tax')
        );

        // Detect linked proforma or quotation source document BEFORE using stored HTML,
        // so we can check whether the stored HTML reflects the latest proforma amounts.
        const proformaDoc = requests.find((x: any) =>
          x.requestType === 'proforma' && (
            String(x._id) === String(targetR.sourceProformaId || targetR.parentProformaId) ||
            String(x.id) === String(targetR.sourceProformaId || targetR.parentProformaId) ||
            (targetR.sourceQuotationId && String(x.sourceQuotationId) === String(targetR.sourceQuotationId)) ||
            (targetR.sourceQuotationNumber && x.sourceQuotationNumber === targetR.sourceQuotationNumber)
          )
        ) || (targetR.requestType === 'proforma' ? targetR : (r.requestType === 'proforma' ? r : null));

        const quotDoc = (targetR as any)._quotDoc || (r as any)._quotDoc || (targetR.requestType === 'quotation' ? targetR : (r.requestType === 'quotation' ? r : null));
        const effectiveSourceDoc = proformaDoc || quotDoc;

        const taxMsList = (effectiveSourceDoc as any)?.paymentSchedule?.milestones || (effectiveSourceDoc as any)?.template_data?.milestones || (effectiveSourceDoc as any)?.milestones || (targetR as any).paymentSchedule?.milestones || (targetR as any).template_data?.milestones || (targetR as any).milestones || (r as any).milestones || [];
        const rawTaxMIdx = milestoneIdx !== undefined ? milestoneIdx : ((targetR as any).milestoneIndex ?? (targetR as any).template_data?.milestoneIndex ?? (r as any).milestoneIndex ?? inferMilestoneIndex(targetR) ?? inferMilestoneIndex(r));
        const activeTaxMilestoneIndex = (rawTaxMIdx !== undefined && rawTaxMIdx !== null && !isNaN(Number(rawTaxMIdx))) ? Number(rawTaxMIdx) : null;
        const activeTaxMilestone = (activeTaxMilestoneIndex !== null && Array.isArray(taxMsList) && taxMsList.length > activeTaxMilestoneIndex) ? taxMsList[activeTaxMilestoneIndex] : null;

        const taxStagePct = activeTaxMilestone
          ? Number(activeTaxMilestone.percent ?? (activeTaxMilestone as any).pct ?? (activeTaxMilestone as any).percentage ?? 0)
          : 0;
        const taxStageName = activeTaxMilestone
          ? (activeTaxMilestone.name || (activeTaxMilestone as any).label || `Stage ${(activeTaxMilestoneIndex ?? 0) + 1}`)
          : undefined;

        // Detect staleness: if the proforma was revised (discounted) after the TI was generated,
        // the stored TI HTML will show the pre-discount amount. In that case, regenerate.
        const proformaSubtotalForTI = Number((proformaDoc as any)?.subtotal || 0);
        const tiStoredSubtotal = Number((targetR as any).subtotal || 0);
        const tiHtmlIsStale = isRealTaxHtml && proformaDoc &&
          proformaSubtotalForTI > 0 && tiStoredSubtotal > 0 &&
          Math.abs(proformaSubtotalForTI - tiStoredSubtotal) > 0.5;

        if (isRealTaxHtml && !tiHtmlIsStale && !activeTaxMilestone && milestoneIdx === undefined) {
          htmlToShow = storedInvoiceHtml;
        } else if (activeTaxMilestone && taxStagePct > 0) {
            const rawItems = (effectiveSourceDoc?.items && effectiveSourceDoc.items.length > 0) ? effectiveSourceDoc.items : ((targetR.items && targetR.items.length > 0) ? targetR.items : (r.items && r.items.length > 0 ? r.items : undefined));
            const fullContractTaxable = Number(
              effectiveSourceDoc?.subtotal || effectiveSourceDoc?.taxableAmount ||
              (effectiveSourceDoc?.total && effectiveSourceDoc?.taxTotal ? effectiveSourceDoc.total - effectiveSourceDoc.taxTotal : 0) ||
              (targetR.subtotal && taxStagePct > 0 ? Math.round((targetR.subtotal / (taxStagePct / 100)) * 100) / 100 : 0) ||
              targetR.subtotal || r.subtotal || 0
            );
            const fullContractGrand = Number(
              effectiveSourceDoc?.total || effectiveSourceDoc?.grandTotal ||
              (targetR.total && taxStagePct > 0 ? Math.round((targetR.total / (taxStagePct / 100)) * 100) / 100 : 0) ||
              targetR.total || r.total || 0
            );
            const fullContractTax = Number(
              effectiveSourceDoc?.taxTotal ||
              (fullContractGrand && fullContractTaxable ? fullContractGrand - fullContractTaxable : 0) ||
              0
            );

            let taxHtml = generateFullTaxInvoiceHtml({
              documentNumber: activeInvoiceNo,
              invoiceNumber: activeInvoiceNo,
              customerName: targetR.clientName || r.clientName || effectiveSourceDoc?.clientName || 'RBM Oil Corporation',
              customerEmail: targetR.clientEmail || r.clientEmail || effectiveSourceDoc?.clientEmail,
              customerContact: targetR.clientPhone || r.clientPhone || effectiveSourceDoc?.clientPhone,
              customerAddress: targetR.clientAddress || r.clientAddress || effectiveSourceDoc?.clientAddress,
              customerGstin: targetR.clientGstin || r.clientGstin || effectiveSourceDoc?.clientGstin,
              items: rawItems && rawItems.length > 0 ? rawItems : undefined,
              notes: targetR.notes || (targetR as any).template_data?.notes || r.notes || (r as any).template_data?.notes || effectiveSourceDoc?.notes || undefined,
              terms: (targetR as any).terms || (targetR as any).template_data?.terms || (r as any).terms || (r as any).template_data?.terms || (effectiveSourceDoc as any)?.terms,
              termsList: (targetR as any).termsList || (targetR as any).template_data?.termsList || (r as any).termsList || (r as any).template_data?.termsList || (effectiveSourceDoc as any)?.termsList,
              assumptions: (targetR as any).assumptions || (targetR as any).template_data?.assumptions || (r as any).assumptions || (r as any).template_data?.assumptions || (effectiveSourceDoc as any)?.assumptions,
              assumptionsList: (targetR as any).assumptionsList || (targetR as any).template_data?.assumptionsList || (r as any).assumptionsList || (r as any).template_data?.assumptionsList || (effectiveSourceDoc as any)?.assumptionsList,
              declaration: (targetR as any).declaration || (targetR as any).template_data?.declaration || (r as any).declaration || (r as any).template_data?.declaration || (effectiveSourceDoc as any)?.declaration,
              // stagePercent scales item amounts so CGST, SGST, grand total & words are milestone-scoped
              stagePercent: taxStagePct,
              milestoneIndex: activeTaxMilestoneIndex ?? undefined,
              stageAmountsAreFinal: activeTaxMilestoneIndex !== null,
              milestones: taxMsList.length > 0 ? taxMsList : undefined,
              signatureUrl: coSig,
              companyLogo: coLogo,
              companyState: coState,
              contractTaxable: fullContractTaxable > 0 ? fullContractTaxable : undefined,
              contractGrand: fullContractGrand > 0 ? fullContractGrand : undefined,
              contractTax: fullContractTax > 0 ? fullContractTax : undefined,
              // Bank details from Finance Master Data
              bankAccName: coBankAccName,
              bankAccNo: coBankAccNo,
              bankName: coBankName,
              bankBranch: coBankBranch,
              bankIfsc: coBankIfsc,
              bankUpi: coBankUpi,
            });
            // Annotate milestone stage under Tax Invoice heading
            taxHtml = taxHtml.replace(
              /(<h1[^>]*>Tax Invoice<\/h1>)/i,
              `$1<p style="margin:2px 0 8px;font-size:12px;color:#555;font-style:italic;">Milestone: ${taxStageName} (${taxStagePct}% of contract)</p>`
            );
            htmlToShow = taxHtml;
          } else {
            const msList = taxMsList;
            let taxHtml = generateFullTaxInvoiceHtml({
              documentNumber: activeInvoiceNo,
              invoiceNumber: activeInvoiceNo,
              customerName: targetR.clientName || r.clientName || 'RBM Oil Corporation',
              customerEmail: targetR.clientEmail || r.clientEmail,
              customerContact: targetR.clientPhone || r.clientPhone,
              customerAddress: targetR.clientAddress || r.clientAddress,
              customerGstin: targetR.clientGstin || r.clientGstin,
              items: targetR.items && targetR.items.length > 0 ? targetR.items : (r.items && r.items.length > 0 ? r.items : undefined),
              notes: targetR.notes || (targetR as any).template_data?.notes || r.notes || (r as any).template_data?.notes || undefined,
              terms: (targetR as any).terms || (targetR as any).template_data?.terms || (r as any).terms || (r as any).template_data?.terms,
              termsList: (targetR as any).termsList || (targetR as any).template_data?.termsList || (r as any).termsList || (r as any).template_data?.termsList,
              assumptions: (targetR as any).assumptions || (targetR as any).template_data?.assumptions || (r as any).assumptions || (r as any).template_data?.assumptions,
              assumptionsList: (targetR as any).assumptionsList || (targetR as any).template_data?.assumptionsList || (r as any).assumptionsList || (r as any).template_data?.assumptionsList,
              declaration: (targetR as any).declaration || (targetR as any).template_data?.declaration || (r as any).declaration || (r as any).template_data?.declaration,
              subtotal: targetR.subtotal || r.subtotal,
              taxTotal: targetR.taxTotal || r.taxTotal,
              total: targetR.total || r.total,
              milestones: msList.length > 0 ? msList : undefined,
              signatureUrl: coSig,
              companyLogo: coLogo,
              companyState: coState,
              // Bank details from Finance Master Data
              bankAccName: coBankAccName,
              bankAccNo: coBankAccNo,
              bankName: coBankName,
              bankBranch: coBankBranch,
              bankIfsc: coBankIfsc,
              bankUpi: coBankUpi,
            });
            htmlToShow = taxHtml;
          }
        if (activeInvoiceNo && htmlToShow) {
          htmlToShow = htmlToShow.replace(/(<span[^>]*id=["']dno["'][^>]*>)[^<]*(<\/span>)/gi, `$1${activeInvoiceNo}$2`);
        }
      } else if (docType === 'proforma') {
        const activeProformaNo = explicitDocNo || targetR.proformaNumber || (targetR.number && /^PI/i.test(targetR.number) ? targetR.number : undefined) || (r.proformaNumber ? r.proformaNumber : 'DT/PI/2026-27/001');
        const storedProformaHtml = (targetR as any).proformaHtmlBody || (targetR as any).currentHtmlBody || (targetR as any).htmlBody || '';
        const isPlaceholderText = (txt: string) => {
          if (!txt) return false;
          const lower = txt.toLowerCase();
          return lower.includes('full 100% payment on delivery') || lower.includes('payment on delivery / order for quotation');
        };
        // If the proforma charges GST but the stored HTML was baked without tax columns
        // (e.g. auto-converted from a GST-off quotation), the preview would show totals and
        // milestone splits without tax — discard it so it regenerates from the record.
        const proformaWantsGst = (targetR as any).gstEnabled !== false && !(targetR as any).reverseCharge;
        const storedHtmlLacksTax = Boolean(storedProformaHtml) && !storedProformaHtml.includes('data-tax');
        const storedProformaHtmlUsable = Boolean(storedProformaHtml) && !isPlaceholderText(storedProformaHtml) && !(proformaWantsGst && storedHtmlLacksTax);

        // Resolve quotation document and milestone list
        const quotDoc = (targetR as any)._quotDoc || (r as any)._quotDoc
          || (targetR.requestType === 'quotation' ? targetR : (r.requestType === 'quotation' ? r : null))
          || rawList.find((x: any) => (x.requestType === 'quotation' || (x.number && /^QT/i.test(x.number))) && (
            (targetR.sourceQuotationNumber && x.number === targetR.sourceQuotationNumber) ||
            (targetR.sourceQuotationId && String(x._id || x.id) === String(targetR.sourceQuotationId)) ||
            (r.sourceQuotationNumber && x.number === r.sourceQuotationNumber) ||
            (r.sourceQuotationId && String(x._id || x.id) === String(r.sourceQuotationId))
          ));
        const msListFromQuot = (quotDoc as any)?.paymentSchedule?.milestones || (quotDoc as any)?.template_data?.milestones || (quotDoc as any)?.milestones || [];
        const msListFromProforma = (targetR as any).paymentSchedule?.milestones || (targetR as any).template_data?.milestones || (targetR as any).milestones || (r as any).milestones || [];
        // Prefer the proforma's own stored milestone stages over the quotation's milestones
        // because the proforma may have customised stage names/percentages.
        const msList = (msListFromProforma.length > 0 ? msListFromProforma : msListFromQuot);

        // Detect active milestone index on this proforma request.
        // The milestoneIdx argument from the button click is the most reliable source —
        // it comes directly from the milestone row's array index in the UI.
        const rawMIdx = milestoneIdx !== undefined
          ? milestoneIdx
          : ((targetR as any).milestoneIndex ?? (targetR as any).template_data?.milestoneIndex ?? (r as any).milestoneIndex ?? inferMilestoneIndex(targetR) ?? inferMilestoneIndex(r));
        const activeMilestoneIndex = (rawMIdx !== undefined && rawMIdx !== null && !isNaN(Number(rawMIdx))) ? Number(rawMIdx) : null;
        const activeMilestone = (activeMilestoneIndex !== null && Array.isArray(msList) && msList.length > activeMilestoneIndex) ? msList[activeMilestoneIndex] : null;

        // If a specific milestone is active, get its percentage to scale the whole document
        const stagePct = activeMilestone
          ? Number(activeMilestone.percent ?? (activeMilestone as any).pct ?? (activeMilestone as any).percentage ?? 0)
          : 0;
        const stageName = activeMilestone
          ? (activeMilestone.name || (activeMilestone as any).label || `Stage ${(activeMilestoneIndex ?? 0) + 1}`)
          : undefined;

        const rawItems = (targetR.items && targetR.items.length > 0 && !targetR.items.every((it: any) => isPlaceholderText(it?.name || it?.description || '')))
          ? targetR.items
          : ((targetR as any).lines && (targetR as any).lines.length > 0
            ? (targetR as any).lines
            : ((targetR as any).template_data?.items && (targetR as any).template_data.items.length > 0
              ? (targetR as any).template_data.items
              : ((targetR as any).template_data?.lines && (targetR as any).template_data.lines.length > 0
                ? (targetR as any).template_data.lines
                : (r.items && r.items.length > 0 && !r.items.every((it: any) => isPlaceholderText(it?.name || it?.description || '')))
                  ? r.items
                  : ((r as any).lines && (r as any).lines.length > 0
                    ? (r as any).lines
                    : ((r as any).template_data?.items && (r as any).template_data.items.length > 0
                      ? (r as any).template_data.items
                      : ((r as any).template_data?.lines && (r as any).template_data.lines.length > 0
                        ? (r as any).template_data.lines
                        : ((quotDoc as any)?.items || (quotDoc as any)?.lines || (quotDoc as any)?.template_data?.items || (quotDoc as any)?.template_data?.lines || undefined)))))));

        const cleanItems = Array.isArray(rawItems) ? rawItems.filter((it: any) => !isPlaceholderText(it?.name || it?.description || '')).map((it: any) => {
          const name = it.name || it.description || 'Deliverable / service';
          // Keep the per-line note visible: it only renders when carried inside subDescription.
          const subDesc = printedLineNote(it);
          return {
            name,
            description: name,
            subDescription: subDesc,
            notes: it.notes || '',
            hsn: it.hsn || it.hsnSac || '998314',
            quantity: Number(it.quantity ?? it.qty ?? 1),
            unit: it.unit || 'Nos',
            unitPrice: Number(it.unitPrice ?? it.rate ?? 0),
            discountPercent: Number(it.discountPercent ?? (it.discount !== undefined ? it.discount : (it.disc ?? 0))),
            gstRate: Number(it.gstRate ?? (it.taxRate !== undefined ? it.taxRate : (((it.cgstRate || 0) + (it.sgstRate || 0) + (it.igstRate || 0)) || 18))),
          };
        }) : undefined;
        const finalItems = (cleanItems && cleanItems.length > 0) ? cleanItems : undefined;

        // Derive full contract totals from the PROFORMA's own (discounted) totals.
        // This ensures the milestone schedule table always splits the proforma's actual discounted
        // grand total proportionally — not the stale pre-discount milestone stored values.
        const proformaSubtotal  = Number(targetR.subtotal || r.subtotal || 0);
        const proformaGrandTotal = Number(targetR.total   || r.total   || 0);
        const proformaTaxTotal  = Number(targetR.taxTotal  || r.taxTotal  || 0);

        // Apply header discount to arrive at the actual post-discount amounts.
        // targetR.total stores the pre-header-discount value; the template applies
        // the header discount visually. We need the true payable amount for the milestone table.
        const effectiveHeaderDiscount = (targetR as any).headerDiscount ?? (targetR as any).template_data?.headerDiscount ?? (quotDoc as any)?.headerDiscount ?? (quotDoc as any)?.template_data?.headerDiscount ?? 0;
        const effectiveHeaderDiscountType = (targetR as any).headerDiscountType ?? (targetR as any).template_data?.headerDiscountType ?? (quotDoc as any)?.headerDiscountType ?? (quotDoc as any)?.template_data?.headerDiscountType ?? 'percent';

        // Stored proforma totals are the authoritative post-discount amounts — both the proforma
        // form and the server store header-discount-applied values. Do not discount them again.
        const discountedSubtotal   = proformaSubtotal;
        const discountedTaxTotal   = proformaTaxTotal;
        const discountedGrandTotal = proformaGrandTotal;

        // When this proforma covers a specific milestone stage (stagePct > 0), un-scale back to
        // the full contract value so that ALL milestone rows in the schedule table are correct.
        const fullContractTaxable = Number(
          stagePct > 0 && stagePct < 100 && discountedSubtotal > 0
            ? Math.round((discountedSubtotal / (stagePct / 100)) * 100) / 100
            : (discountedSubtotal || (quotDoc as any)?.subtotal || 0)
        ) || undefined;
        const fullContractGrand = Number(
          stagePct > 0 && stagePct < 100 && discountedGrandTotal > 0
            ? Math.round((discountedGrandTotal / (stagePct / 100)) * 100) / 100
            : (discountedGrandTotal || (quotDoc as any)?.total || 0)
        ) || undefined;
        const fullContractTax = Number(
          stagePct > 0 && stagePct < 100 && discountedTaxTotal > 0
            ? Math.round((discountedTaxTotal / (stagePct / 100)) * 100) / 100
            : (discountedTaxTotal || (quotDoc as any)?.taxTotal || ((fullContractGrand && fullContractTaxable) ? fullContractGrand - fullContractTaxable : 0) || 0)
        ) || undefined;


        const effectiveDiscount = (targetR as any).discount ?? (targetR as any).template_data?.discount ?? (quotDoc as any)?.discount ?? (quotDoc as any)?.template_data?.discount;


        // Trust the stored snapshot by default. Regeneration only happens when there is no usable
        // stored HTML (legacy docs) or when a specific milestone stage view was explicitly
        // requested — stage-scoped rendering is a deliberate view mode, not the stored artifact.
        if (storedProformaHtmlUsable && milestoneIdx === undefined) {
          htmlToShow = storedProformaHtml;
        } else if (activeMilestone && stagePct > 0) {
          htmlToShow = generateFullProformaInvoiceHtml({
            documentNumber: activeProformaNo,
            proformaNumber: activeProformaNo,
            customerName: targetR.clientName || r.clientName || quotDoc?.clientName || 'RBM Oil Corporation',
            customerEmail: targetR.clientEmail || r.clientEmail || quotDoc?.clientEmail,
            customerContact: targetR.clientPhone || r.clientPhone || quotDoc?.clientPhone,
            customerAddress: targetR.clientAddress || r.clientAddress || quotDoc?.clientAddress,
            customerGstin: targetR.clientGstin || r.clientGstin || quotDoc?.clientGstin,
            items: finalItems,
            notes: targetR.notes || (targetR as any).template_data?.notes || r.notes || (r as any).template_data?.notes || quotDoc?.notes || undefined,
            terms: (targetR as any).terms || (targetR as any).template_data?.terms || (r as any).terms || (r as any).template_data?.terms || (quotDoc as any)?.terms,
            termsList: (targetR as any).termsList || (targetR as any).template_data?.termsList || (r as any).termsList || (r as any).template_data?.termsList || (quotDoc as any)?.termsList,
            assumptions: (targetR as any).assumptions || (targetR as any).template_data?.assumptions || (r as any).assumptions || (r as any).template_data?.assumptions || (quotDoc as any)?.assumptions,
            assumptionsList: (targetR as any).assumptionsList || (targetR as any).template_data?.assumptionsList || (r as any).assumptionsList || (r as any).template_data?.assumptionsList || (quotDoc as any)?.assumptionsList,
            declaration: (targetR as any).declaration || (targetR as any).template_data?.declaration || (r as any).declaration || (r as any).template_data?.declaration || (quotDoc as any)?.declaration,
            subtotal: targetR.subtotal || r.subtotal,
            taxTotal: targetR.taxTotal || r.taxTotal,
            grandTotal: targetR.total || r.total,
            stagePercent: stagePct,
            milestoneIndex: activeMilestoneIndex ?? undefined,
            stageAmountsAreFinal: false,
            // GST comes from the proforma itself; without it the template charges GST by place of supply.
            gstEnabled: (targetR as any).gstEnabled,
            gstMode: (targetR as any).gstEnabled === false ? 'none' : ((targetR as any).gstMode || undefined),
            customerState: (targetR as any).placeOfSupply || (targetR as any).clientState || (r as any).placeOfSupply || (r as any).clientState,
            companyGstin: (targetR as any).businessGstin || (r as any).businessGstin,
            headerDiscount: effectiveHeaderDiscount,
            headerDiscountType: effectiveHeaderDiscountType,
            additionalCharges: Number((targetR as any).additionalCharges ?? (r as any).additionalCharges ?? 0) || undefined,
            additionalChargesLabel: (targetR as any).additionalChargesLabel || (r as any).additionalChargesLabel || undefined,
            discount: effectiveDiscount,
            // Explicit full-contract totals so the milestone schedule table splits correctly
            contractTaxable: fullContractTaxable,
            contractGrand:   fullContractGrand,
            contractTax:     fullContractTax,
            // Still pass milestone list so the payment schedule table renders fully
            paymentType: msList.length > 0 ? 'milestone_based' : 'full_payment',
            milestones: msList.length > 0 ? msList : undefined,
            signatureUrl: coSig,
            companyLogo: coLogo,
            companyState: coState,
            // Bank details from Finance Master Data
            bankAccName: coBankAccName,
            bankAccNo: coBankAccNo,
            bankName: coBankName,
            bankBranch: coBankBranch,
            bankIfsc: coBankIfsc,
            bankUpi: coBankUpi,
          });
        } else {
          // Always regenerate from stored proforma data so the milestone table reflects
          // the current discounted amounts (contractTaxable/Grand/Tax = proforma's own totals).

          // Detect paymentType from the stored proforma document, then fall back to msList presence.
          const proformaPaymentType = (targetR as any).paymentType ||
            (targetR as any).template_data?.paymentType ||
            (targetR as any).paymentSchedule?.paymentType ||
            (r as any).paymentType ||
            (msList.length > 0 ? 'milestone_based' : 'full_payment');
          const isMilestonePF = proformaPaymentType === 'milestone_based' ||
            (msList.length > 0 && proformaPaymentType !== 'full_payment');

          htmlToShow = generateFullProformaInvoiceHtml({
            documentNumber: activeProformaNo,
            proformaNumber: activeProformaNo,
            customerName: targetR.clientName || r.clientName || quotDoc?.clientName || 'RBM Oil Corporation',
            customerEmail: targetR.clientEmail || r.clientEmail || quotDoc?.clientEmail,
            customerContact: targetR.clientPhone || r.clientPhone || quotDoc?.clientPhone,
            customerAddress: targetR.clientAddress || r.clientAddress || quotDoc?.clientAddress,
            customerGstin: targetR.clientGstin || r.clientGstin || quotDoc?.clientGstin,
            items: finalItems,
            notes: targetR.notes || (targetR as any).template_data?.notes || r.notes || (r as any).template_data?.notes || quotDoc?.notes || undefined,
            terms: (targetR as any).terms || (targetR as any).template_data?.terms || (r as any).terms || (r as any).template_data?.terms || (quotDoc as any)?.terms,
            termsList: (targetR as any).termsList || (targetR as any).template_data?.termsList || (r as any).termsList || (r as any).template_data?.termsList || (quotDoc as any)?.termsList,
            assumptions: (targetR as any).assumptions || (targetR as any).template_data?.assumptions || (r as any).assumptions || (r as any).template_data?.assumptions || (quotDoc as any)?.assumptions,
            assumptionsList: (targetR as any).assumptionsList || (targetR as any).template_data?.assumptionsList || (r as any).assumptionsList || (r as any).template_data?.assumptionsList || (quotDoc as any)?.assumptionsList,
            declaration: (targetR as any).declaration || (targetR as any).template_data?.declaration || (r as any).declaration || (r as any).template_data?.declaration || (quotDoc as any)?.declaration,
            subtotal: proformaSubtotal || (quotDoc as any)?.subtotal,
            taxTotal: proformaTaxTotal || (quotDoc as any)?.taxTotal,
            grandTotal: proformaGrandTotal || (quotDoc as any)?.total,
            total: proformaGrandTotal || (quotDoc as any)?.total,
            stageAmountsAreFinal: false,
            // GST comes from the proforma itself; without it the template charges GST by place of supply.
            gstEnabled: (targetR as any).gstEnabled,
            gstMode: (targetR as any).gstEnabled === false ? 'none' : ((targetR as any).gstMode || undefined),
            customerState: (targetR as any).placeOfSupply || (targetR as any).clientState || (r as any).placeOfSupply || (r as any).clientState,
            companyGstin: (targetR as any).businessGstin || (r as any).businessGstin,
            headerDiscount: effectiveHeaderDiscount,
            headerDiscountType: effectiveHeaderDiscountType,
            additionalCharges: Number((targetR as any).additionalCharges ?? (r as any).additionalCharges ?? 0) || undefined,
            additionalChargesLabel: (targetR as any).additionalChargesLabel || (r as any).additionalChargesLabel || undefined,
            discount: effectiveDiscount,
            // Use proforma's own discounted totals as contract base so milestone table
            // splits the discounted grand total (e.g. 50% × ₹33,630 = ₹16,815, not ₹17,700)
            contractTaxable: fullContractTaxable,
            contractGrand:   fullContractGrand,
            contractTax:     fullContractTax,
            // Use stored paymentType so milestone table shows for milestone-based proformas
            paymentType: isMilestonePF ? 'milestone_based' : 'full_payment',
            milestones: isMilestonePF && msList.length > 0 ? msList : undefined,
            signatureUrl: coSig,
            companyLogo: coLogo,
            companyState: coState,
            // Bank details from Finance Master Data
            bankAccName: coBankAccName,
            bankAccNo: coBankAccNo,
            bankName: coBankName,
            bankBranch: coBankBranch,
            bankIfsc: coBankIfsc,
            bankUpi: coBankUpi,
          });
        }

        if (activeProformaNo && htmlToShow) {
          htmlToShow = htmlToShow.replace(/(<span[^>]*id=["']dno["'][^>]*>)[^<]*(<\/span>)/gi, `$1${activeProformaNo}$2`);
          htmlToShow = htmlToShow.replace(/(Proforma\s+no\.?:?\s*<\/span>\s*<span[^>]*>)[^<]*(<\/span>)/gi, `$1${activeProformaNo}$2`);
          htmlToShow = htmlToShow.replace(/<span>Invoice no\.<\/span>/gi, '<span>Proforma no.</span>');
          // Annotate which milestone this proforma is for (title injection)
          if (stageName && stagePct > 0) {
            htmlToShow = htmlToShow.replace(
              /(<h1[^>]*>Proforma Invoice<\/h1>)/i,
              `$1<p style="margin:2px 0 8px;font-size:12px;color:#555;font-style:italic;">Milestone: ${stageName} (${stagePct}% of contract)</p>`
            );
          }
        }
      } else {
        const lastRevision = (targetR as any).documentRevisions?.[(targetR as any).documentRevisions?.length - 1];
        // Trust the stored snapshot — it is the artifact that was submitted and approved.
        htmlToShow = targetR.currentHtmlBody || (targetR as any).htmlBody || lastRevision?.htmlBody || '';

        const rawMilestones = (targetR as any).template_data?.milestones
          || (targetR as any).milestones
          || (targetR as any).paymentSchedule?.milestones
          || (r as any).template_data?.milestones
          || (r as any).milestones
          || (r as any).paymentSchedule?.milestones
          || [];

        const subtotalAmt = Number(targetR.subtotal || r.subtotal || 20000);
        const taxAmt = Number(targetR.taxTotal || r.taxTotal || Math.round(subtotalAmt * 0.18));
        const grandAmt = Number(targetR.total || r.total || (subtotalAmt + taxAmt));

        const enrichedMsList = rawMilestones.map((m: any, idx: number) => {
          const pct = Number(m.percent ?? m.percentage ?? m.pct ?? (rawMilestones.length > 0 ? (100 / rawMilestones.length) : 25));
          const mNet = Number(m.netAmount || m.milestoneTaxableAmount || Math.round(subtotalAmt * (pct / 100)));
          const mTax = Number(m.taxAmount || m.milestoneGST || Math.round(taxAmt * (pct / 100)));
          const mDue = Number(m.amountDue || m.amount || m.milestoneTotal || (mNet + mTax) || Math.round(grandAmt * (pct / 100)));
          return {
            ...m,
            name: m.name || m.label || `Stage ${idx + 1}`,
            percentage: pct,
            percent: pct,
            netAmount: mNet,
            taxAmount: mTax,
            amountDue: mDue,
            amount: mDue,
            milestoneTotal: mDue,
            milestoneTaxableAmount: mNet,
            due: m.due || (m.dueDate ? (String(m.dueDate).includes('T') ? String(m.dueDate).split('T')[0] : String(m.dueDate)) : (m.daysFromApproval ? `T + ${m.daysFromApproval} days` : '—')),
          };
        });

        if (!htmlToShow) {
          const safeFormatDate = (raw: any): string | undefined => {
            if (!raw) return undefined;
            try {
              const dt = new Date(raw);
              if (isNaN(dt.getTime())) return typeof raw === 'string' ? raw : undefined;
              return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            } catch {
              return typeof raw === 'string' ? raw : undefined;
            }
          };

          const docDateStr = safeFormatDate(targetR.date) || safeFormatDate(targetR.requestedAt);
          const validUntilStr = safeFormatDate((targetR as any).validTillDate) || safeFormatDate((targetR as any).validUntil);

          htmlToShow = generateFullQuotationHtml({
            quotationNumber: baseQuotNumber,
            quotationDate: docDateStr,
            validUntil: validUntilStr,
            companyName: (targetR as any).businessName,
            companyTag: (targetR as any).businessTag,
            companyAddress: (targetR as any).businessAddress,
            companyGstin: (targetR as any).businessGstin,
            companyPan: (targetR as any).businessPan,
            companyPhone: (targetR as any).businessPhone,
            companyEmail: (targetR as any).businessEmail,
            companyWeb: (targetR as any).businessWeb,
            signatureUrl: coSig,
            companyLogo: coLogo,
            companyState: coState,
            customerName: targetR.clientName || '',
            customerEmail: targetR.clientEmail,
            customerPhone: targetR.clientPhone,
            customerAddress: targetR.clientAddress,
            customerGstin: targetR.clientGstin,
            customerState: (targetR as any).placeOfSupply || targetR.clientState,
            contactPerson: (targetR as any).contactPerson,
            contactDesignation: (targetR as any).contactDesignation,
            preparedBy: (targetR as any).preparedBy || targetR.requestedByName,
            subject: (targetR as any).subject,
            introText: (targetR as any).introText,
            items: targetR.items && targetR.items.length > 0 ? targetR.items : (r.items && r.items.length > 0 ? r.items : undefined),
            notes: targetR.notes || (targetR as any).template_data?.notes || r.notes || (r as any).template_data?.notes || undefined,
            subtotal: subtotalAmt,
            taxTotal: taxAmt,
            grandTotal: grandAmt,
            total: grandAmt,
            headerDiscount: (targetR as any).headerDiscount ?? (targetR as any).template_data?.headerDiscount ?? 0,
            headerDiscountType: (targetR as any).headerDiscountType ?? (targetR as any).template_data?.headerDiscountType ?? 'percent',
            additionalCharges: Number((targetR as any).additionalCharges ?? (r as any).additionalCharges ?? 0) || undefined,
            additionalChargesLabel: (targetR as any).additionalChargesLabel || (r as any).additionalChargesLabel || undefined,
            paymentType: enrichedMsList.length > 0 ? 'milestone_based' : 'full_payment',
            milestones: enrichedMsList.length > 0 ? enrichedMsList : undefined,
            gstEnabled: (targetR as any).gstEnabled,
            gstMode: (targetR as any).gstEnabled === false ? 'none' : ((targetR as any).reverseCharge ? 'none' : ((targetR as any).gstMode || 'intra')),
            reverseCharge: (targetR as any).reverseCharge ? 'Yes' : 'No',
            assumptions: (targetR as any).assumptions || (targetR as any).template_data?.assumptions || (r as any)?.assumptions || (r as any)?.template_data?.assumptions,
            assumptionsList: (targetR as any).assumptionsList || (targetR as any).template_data?.assumptionsList || (r as any)?.assumptionsList || (r as any)?.template_data?.assumptionsList,
            terms: (targetR as any).terms || (targetR as any).template_data?.terms || (r as any)?.terms || (r as any)?.template_data?.terms,
            termsList: (targetR as any).termsList || (targetR as any).template_data?.termsList || (r as any)?.termsList || (r as any)?.template_data?.termsList,
            declaration: (targetR as any).declaration || (targetR as any).template_data?.declaration || (r as any)?.declaration || (r as any)?.template_data?.declaration,
            bankAccName: (targetR as any).bankAccName,
            bankAccNo: (targetR as any).bankAccNo,
            bankName: (targetR as any).bankName || (targetR as any).template_data?.bankName,
            bankBranch: (targetR as any).bankBranch,
            bankIfsc: (targetR as any).bankIfsc,
            bankUpi: (targetR as any).bankUpi,
            signatory: (targetR as any).signatory,
            signatoryRole: (targetR as any).signatoryRole,
            footerLeft: (targetR as any).footerLeft,
            footerRight: (targetR as any).footerRight,
          });
        }

        if (htmlToShow) {
          htmlToShow = htmlToShow.replace(/(<span[^>]*id=["']dno["'][^>]*>)\s*(?:TI|PI)[^<]*(<\/span>)/gi, `$1${baseQuotNumber}$2`);
          htmlToShow = htmlToShow.replace(/(Quotation\s+no\.?:?\s*<\/span>\s*<span[^>]*>)\s*(?:TI|PI)[^<]*(<\/span>)/gi, `$1${baseQuotNumber}$2`);
          htmlToShow = htmlToShow.replace(/(<title>Quotation\s+)(?:TI|PI)[^—<]*(—)/gi, `$1${baseQuotNumber} $2`);
        }
      }

      const isProfPending = ['proforma_requested', 'proforma_under_review'].includes(targetR.status);
      const canRejectProforma = Boolean(targetR.canRejectNow || (isSuperAdmin && isProfPending));


      setSelectedDocData({
        id: targetR.id || String((targetR as any)._id || r.id || (r as any)._id || ''),
        number: docType === 'quotation' ? baseQuotNumber : (targetR.number || r.number),
        documentNumber: docType === 'proforma' ? (targetR.proformaNumber || targetR.number) : docType === 'quotation' ? baseQuotNumber : (targetR.invoiceNumber || targetR.number),
        clientName: targetR.clientName || r.clientName,
        customerName: targetR.clientName || r.clientName,
        status: targetR.status || r.status || 'draft',
        requestType: docType,
        currentHtmlBody: htmlToShow,
        subtotal: targetR.subtotal || r.subtotal,
        taxTotal: targetR.taxTotal || r.taxTotal,
        total: targetR.total || r.total,
        notes: targetR.notes || r.notes,
        reviewNote: targetR.reviewNote || r.reviewNote,
        items: targetR.items && targetR.items.length > 0 ? targetR.items : r.items,
        history: targetR.history || r.history || [],
        documentRevisions: targetR.documentRevisions || r.documentRevisions || [],
        // Quotations need no approval, and a proforma is only returned, never approved.
        canApproveNow: docType === 'quotation' ? false : targetR.canApproveNow,
        canRejectNow: docType === 'proforma' ? canRejectProforma : (docType === 'quotation' ? false : targetR.canRejectNow),
        canSubmit: targetR.canSubmit || ['draft', 'proforma_draft'].includes(targetR.status),
        canResubmitProforma: targetR.canResubmitProforma || targetR.status === 'proforma_rejected',
        rejectionReason: targetR.reviewNote || (targetR as any).rejectionReason,
        quotationNumber: baseQuotNumber,
        proformaId: (targetR as any).proformaId || r.proformaId,
        emailSentAt: (targetR as any).emailSentAt || (r as any).emailSentAt,
        supersededById: (targetR as any).supersededById || (r as any).supersededById,
        supersededByNumber: (targetR as any).supersededByNumber || (r as any).supersededByNumber,
        proformaNumber: targetR.proformaNumber || (targetR.number?.startsWith('PI') ? targetR.number : undefined),
      });
      setDrawerOpen(true);
    } catch (err) {
      console.error('Error in handleOpenDrawer:', err);
      toast.error('Could not open document preview');
    }
  };

  const handleSaveDrawerHtml = async (id: string, html: string, note?: string, structuredData?: any) => {
    const isProforma = selectedDocData?.requestType === 'proforma' || html.includes('data-doc="proforma"') || html.includes('Proforma Invoice');
    const isTaxInvoice = selectedDocData?.requestType === 'invoice' || html.includes('data-doc="tax"') || html.includes('Tax Invoice');
    const payload: any = {
      htmlBody: html,
      currentHtmlBody: html,
      note,
      isRevision: true,
      ...(structuredData || {}),
    };
    if (isProforma) {
      payload.proformaHtmlBody = html;
    } else if (isTaxInvoice) {
      payload.invoiceHtmlBody = html;
    }
    // Save revision snapshot
    try {
      await api.post(`/invoice-requests/${id}/revision`, payload, { skipErrorToast: true });
    } catch (e) {
      console.warn('Could not save revision snapshot:', e);
    }
    // Also update the live currentHtmlBody and structured fields in DB
    await api.put(`/invoice-requests/${id}`, payload, { skipErrorToast: true });
    await refetch();
    await queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
    await queryClient.invalidateQueries({ queryKey: ['quotations'] });
    await queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
  };

  const handleDrawerApprove = async (id: string, note?: string) => {
    const rawList = data?.requests || [];
    const row = requests.find((x) => x.id === id) || rawList.find((x: any) => x.id === id || String(x._id) === id);
    const stage = (selectedDocData?.requestType as ApprovalStage) || (row ? stageOf(row) : null) || 'quotation';
    if (stage) {
      await actions.approve.mutateAsync({ id, stage, note });
      setDrawerOpen(false);
      refetch();
    }
  };

  const handleDrawerReject = async (id: string, note: string) => {
    const rawList = data?.requests || [];
    const row = requests.find((x) => x.id === id) || rawList.find((x: any) => x.id === id || String(x._id) === id);
    const stage: ApprovalStage = (selectedDocData?.requestType as ApprovalStage) || (row ? stageOf(row) : null) || 'quotation';
    await actions.reject.mutateAsync({ id, stage, reason: note });
    setDrawerOpen(false);
    refetch();
  };

  const handleRequestTaxInvoiceApproval = async (id: string, html?: string, note?: string) => {
    try {
      await api.post(`/invoice-requests/${id}/request-invoice-approval`, { html, note });
      toast.success('Tax invoice approval requested directly from Superadmin.');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'request tax invoice approval'));
      throw err;
    }
  };

  const handleDrawerSubmitDraft = async (id: string) => {
    await actions.submit.mutateAsync({ id });
    setDrawerOpen(false);
    refetch();
  };

  const handleOpenResubmissionFromDrawer = (docData: DocumentWorkspaceData) => {
    setDrawerOpen(false);
    setProformaModalState({
      open: true,
      editingDraft: docData as any,
      isResubmission: true,
      documentId: docData.id,
      rejectionReason: docData.reviewNote || docData.rejectionReason,
      proformaNumber: docData.proformaNumber || docData.documentNumber || docData.number,
      quotationNumber: docData.quotationNumber,
    });
  };

  // The drawer only offers "Edit Proforma" for proforma documents, so this
  // always routes to the full-page edit form.
  const handleEditDraftFromDrawer = (docData: DocumentWorkspaceData) => {
    setDrawerOpen(false);
    navigate(salesProformaEditPath(docData.id || (docData as any)._id));
  };

  const handleReviseProforma = async (r: any, explicitIdx?: number, explicitPhase?: string) => {
    const targetProformaDoc = (r as any)._proformaDoc || (r.requestType === 'proforma' || (r.number && /^PI/i.test(r.number)) ? r : null) || r;
    const docId = targetProformaDoc?._id || targetProformaDoc?.id || r._id || r.id;
    if (!docId) {
      toast.error('Document ID not found');
      return;
    }

    try {
      // Step 1: Call /revise on backend with isProforma: true
      await api.post(`/api/invoice-requests/${docId}/revise`, {
        isProforma: true,
        note: 'Proforma revision initiated from Invoice Requests',
      }, { skipErrorToast: true });

      // Step 2: Fetch the latest saved data from DB (never use stale cache)
      const { data: freshDoc } = await api.get(`/api/invoice-requests/${docId}`);
      const latestDoc = freshDoc?.request || freshDoc?.doc || freshDoc?.data || freshDoc;

      if (!latestDoc) {
        toast.error('Could not load latest proforma data.');
        return;
      }

      // Invalidate queries so lists update immediately
      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['accounting-schedules'] });

      // Step 3: Compute revision labels and open the full proforma form
      const nextRevNumber = latestDoc.currentRevisionNumber
        ? latestDoc.currentRevisionNumber + 1
        : (latestDoc.documentRevisions?.length ? latestDoc.documentRevisions.length + 1 : 2);
      const nextRevLabel = `R${nextRevNumber}`;
      const currentRevLabel = latestDoc.currentRevisionLabel || `R${nextRevNumber - 1}`;

      const qId = latestDoc.sourceQuotationId || latestDoc.quotationId || r.sourceQuotationId || r.id;
      const qNo = latestDoc.sourceQuotationNumber || latestDoc.quotationNumber || r.sourceQuotationNumber || (r.number?.startsWith('QT') ? r.number : undefined);

      const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
      const ROMAN_MAP: Record<string, number> = {
        'I': 0, 'II': 1, 'III': 2, 'IV': 3, 'V': 4,
        'VI': 5, 'VII': 6, 'VIII': 7, 'IX': 8, 'X': 9,
        '1': 0, '2': 1, '3': 2, '4': 3, '5': 4,
        '6': 5, '7': 6, '8': 7, '9': 8, '10': 9,
      };

      const resolveRawIdx = (): number | undefined => {
        if (explicitIdx !== undefined) return explicitIdx;
        const candidates = [latestDoc, targetProformaDoc, r];
        for (const c of candidates) {
          if (!c) continue;
          if (c.milestoneIndex !== undefined && c.milestoneIndex !== null && !isNaN(Number(c.milestoneIndex))) {
            return Number(c.milestoneIndex);
          }
          if (c.template_data?.milestoneIndex !== undefined && c.template_data?.milestoneIndex !== null && !isNaN(Number(c.template_data.milestoneIndex))) {
            return Number(c.template_data.milestoneIndex);
          }
          if (c.paymentSchedule?.milestoneIndex !== undefined && c.paymentSchedule?.milestoneIndex !== null && !isNaN(Number(c.paymentSchedule.milestoneIndex))) {
            return Number(c.paymentSchedule.milestoneIndex);
          }
          const pLabel = c.milestonePhaseLabel || c.template_data?.milestonePhaseLabel;
          if (pLabel) {
            const clean = String(pLabel).replace(/[\(\)]/g, '').trim().toUpperCase();
            if (clean in ROMAN_MAP) return ROMAN_MAP[clean];
            const num = parseInt(clean, 10);
            if (!isNaN(num) && num > 0) return num - 1;
          }
          const numStr = c.proformaNumber || c.number || c.documentNumber || c.subject || '';
          const match = numStr.match(/\(([IVXLCDM]+|\d+)\)/i);
          if (match) {
            const token = match[1].toUpperCase();
            if (token in ROMAN_MAP) return ROMAN_MAP[token];
            const num = parseInt(token, 10);
            if (!isNaN(num) && num > 0) return num - 1;
          }
          const items = c.items || c.lines || c.template_data?.items || c.template_data?.lines;
          if (Array.isArray(items) && items.length === 1) {
            const itemName = items[0]?.name || items[0]?.description || '';
            const lineMatch = itemName.match(/(?:Stage|Phase|Milestone|PI[\s\-_]*\(?)\s*([IVXLCDM]+|\d+)/i);
            if (lineMatch) {
              const token = lineMatch[1].toUpperCase();
              if (token in ROMAN_MAP) return ROMAN_MAP[token];
              const num = parseInt(token, 10);
              if (!isNaN(num) && num > 0) return num - 1;
            }
          }
        }
        return undefined;
      };

      const rawIdx = resolveRawIdx();
      const rawPhase = explicitPhase
        || latestDoc.milestonePhaseLabel
        || latestDoc.template_data?.milestonePhaseLabel
        || r.milestonePhaseLabel
        || (targetProformaDoc as any)?.milestonePhaseLabel
        || (rawIdx !== undefined ? (ROMANS[rawIdx] || String(rawIdx + 1)) : undefined);

      setDrawerOpen(false);
      setProformaModalState({
        open: true,
        editingDraft: latestDoc,
        isRevision: true,
        isResubmission: false,
        documentId: latestDoc._id || latestDoc.id || docId,
        proformaNumber: latestDoc.proformaNumber || latestDoc.number || targetProformaDoc.proformaNumber || targetProformaDoc.number,
        quotationId: qId,
        quotationNumber: qNo,
        milestoneIndex: rawIdx,
        milestonePhaseLabel: rawPhase,
        currentRevisionLabel: currentRevLabel,
        nextRevisionLabel: nextRevLabel,
        rejectionReason: latestDoc.reviewNote || '',
      });
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'start this proforma revision'));
    }
  };

  const handleDrawerResubmit = async (id: string, html: string, note?: string) => {
    const rawList = data?.requests || [];
    const row = requests.find((x) => x.id === id) || rawList.find((x: any) => x.id === id || String(x._id) === id);
    if (html) {
      await api.post(`/api/invoice-requests/${id}/revision`, { htmlBody: html, note }, { skipErrorToast: true });
    }
    if (row?.status === 'proforma_rejected') {
      await actions.resubmitProforma.mutateAsync({ id, note });
    }
  };


  const rawRequests = useMemo(() => data?.requests ?? [], [data]);
  const canReview = data?.canReview ?? false;
  // On a Sales route the accounts-side framing is suppressed even for a user who
  // holds accounts authority, so the page reads correctly in context.
  const isSalesRoute = location.pathname.includes('/sales') || location.pathname.includes('/sales-documents');
  const isAccountsView = canReview && !isSalesRoute;
  const counts = data?.statusCounts ?? {};

  /**
   * Group requests by their chain root (source quotation number / id).
   * Multiple records (QT, PI, TI) for the same deal become ONE card, and only chains that have
   * a proforma are listed.
   * The most advanced record (invoice → proforma → quotation) is the primary
   * row so it drives the status badge and actions; the others are attached as
   * sibling docs so their view-buttons remain reachable.
   */
  const requests = useMemo(() => {
    type Chain = InvoiceRequest & {
      _quotDoc?: InvoiceRequest;
      _proformaDoc?: InvoiceRequest;
      _invoiceDoc?: InvoiceRequest;
    };

    const rank = (r: InvoiceRequest) => {
      // Rank ONLY by requestType — invoiceNumber on a proforma doc must NOT promote it
      if (r.requestType === 'invoice') return 3;
      if (r.requestType === 'proforma') return 2;
      return 1;
    };

    const extractPhaseLabel = (r: InvoiceRequest): string | null => {
      const pType = (r as any).template_data?.paymentType || (r as any).paymentType || (r as any)._quotDoc?.template_data?.paymentType || (r as any)._quotDoc?.paymentType;
      const rawTerms = (r as any).paymentTerms || (r as any).template_data?.paymentTerms || (r as any)._quotDoc?.paymentTerms || (r as any)._quotDoc?.template_data?.paymentTerms || '';
      const isExplicitFull = pType === 'full_payment';
      if (isExplicitFull) return null;
      if (r.milestonePhaseLabel) return r.milestonePhaseLabel.replace(/[\(\)]/g, '').trim().toUpperCase();
      if (r.milestoneIndex !== undefined && r.milestoneIndex !== null) return String(r.milestoneIndex);
      if ((r as any).template_data?.milestoneIndex !== undefined && (r as any).template_data?.milestoneIndex !== null) return String((r as any).template_data.milestoneIndex);
      const str = r.number || r.proformaNumber || r.invoiceNumber || '';
      const match = str.match(/\(([IVXLCDM]+)\)/i);
      if (match) return match[1].toUpperCase();
      return null;
    };

    const chainKey = (r: InvoiceRequest): string => {
      const baseQuot = r.sourceQuotationNumber || r.sourceQuotationId || (r.requestType === 'quotation' ? r.number : '') || r.id;
      const phase = extractPhaseLabel(r);
      if (phase && r.requestType !== 'quotation') {
        return `${baseQuot}_phase_${phase}`;
      }
      return baseQuot;
    };

    const groups = new Map<string, InvoiceRequest[]>();
    for (const r of rawRequests) {
      const k = chainKey(r);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(r);
    }

    const chained: Chain[] = [];
    for (const [, members] of Array.from(groups.entries())) {
      if (members.length === 1) {
        chained.push(members[0] as Chain);
        continue;
      }
      const sorted = [...members].sort((a, b) => rank(b) - rank(a));
      const primary = { ...sorted[0] } as Chain;

      for (const sibling of sorted.slice(1)) {
        if (sibling.requestType === 'quotation') {
          primary._quotDoc = sibling;
          if (!primary.sourceQuotationNumber) primary.sourceQuotationNumber = sibling.number;
          if (!primary.sourceQuotationId) primary.sourceQuotationId = sibling.id || (sibling as any)._id;
        } else if (sibling.requestType === 'proforma') {
          primary._proformaDoc = sibling;
          if (!primary.proformaNumber) primary.proformaNumber = sibling.proformaNumber || sibling.number;
        } else if (sibling.requestType === 'invoice') {
          primary._invoiceDoc = sibling;
          if (!primary.invoiceNumber) primary.invoiceNumber = sibling.invoiceNumber || sibling.number;
        }
      }
      if (primary._quotDoc) {
        if (!primary.sourceQuotationId) primary.sourceQuotationId = primary._quotDoc.id || (primary._quotDoc as any)._id;
        if (!primary.sourceQuotationNumber) primary.sourceQuotationNumber = primary._quotDoc.number;
      }
      chained.push(primary);
    }

    // This page lists proformas only. A quotation (or a tax invoice with no proforma) is not a
    // row here: quotations live on the Quotations page, and a proforma appears once it is created
    // from a quotation.
    const proformasOnly = chained.filter((r) => r.requestType === 'proforma' || Boolean(r._proformaDoc));

    proformasOnly.sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
    return proformasOnly;
  }, [rawRequests]);

  const busy = Object.values(actions).some(m => m.isPending);

  // Distinct salespeople behind the listed proformas, and the rows the chosen one prepared.
  const preparedByOptions = useMemo(
    () => Array.from(new Set(requests.map((r) => preparedByOf(r)).filter(Boolean))).sort() as string[],
    [requests]
  );

  const visibleRequests = useMemo(
    () => (preparedByFilter ? requests.filter((r) => preparedByOf(r) === preparedByFilter) : requests),
    [requests, preparedByFilter]
  );

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    try {
      await fn();
      toast.success(okMsg);
      refetch();
    } catch (err) {
      toast.error(errorText(err, 'Action failed.'));
    }
  };

  const submitRejection = async () => {
    if (!rejecting || !rejectReason.trim()) return;
    const { id, stage } = rejecting;
    await run(
      () => actions.reject.mutateAsync({ id, stage, reason: rejectReason.trim() }),
      stage === 'proforma' ? 'Proforma rejected.' : 'Quotation rejected.',
    );
    setRejecting(null);
    setRejectReason('');
  };

  if (isLoading) return <div className="p-6"><Spinner /></div>;
  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
          title="Could not load invoice & quotation requests"
          description="You may not have access to this queue, or the server is unreachable."
        />
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <AccountingSubNav />
      <div className="py-6 px-2 sm:px-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shadow-sm"
            title="Back"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
            <span>Back</span>
          </button>
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              Proforma Invoices
            </h1>
            <p className="text-xs text-muted-foreground">
              Manage and track proforma invoices.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate('/app/sales/proforma/create')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600"
          >
            <FileText className="h-4 w-4 mr-1.5" /> Create Proforma Invoice
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              filter === f.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-card border-border text-muted-foreground hover:bg-slate-50'
            }`}
          >
            {f.label}
            {f.value && counts[f.value] ? ` (${counts[f.value]})` : ''}
          </button>
        ))}
        </div>

        <div className="relative min-w-[180px]">
          <select
            value={preparedByFilter}
            onChange={(e) => setPreparedByFilter(e.target.value)}
            aria-label="Filter by prepared by"
            className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-card border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer appearance-none"
          >
            <option value="">All Prepared By</option>
            {preparedByOptions.map((name) => (
              <option key={name} value={name}>Prepared by {name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground">
            <ChevronDown className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>

      {visibleRequests.length === 0 ? (
        <EmptyState
          title={preparedByFilter ? `No proforma invoices prepared by ${preparedByFilter}` : 'No proforma invoices'}
          description={
            preparedByFilter
              ? 'Clear the Prepared By filter to see the whole queue.'
              : 'A proforma invoice is created from a quotation. Open the Quotations page and use Create Proforma Invoice on the quotation.'
          }

        />
      ) : (
        <div className="space-y-3">
          {visibleRequests.map((r: InvoiceRequest) => {
            const stage = stageOf(r);
            const isInvoiceReq = r.requestType === 'invoice';
            const statusLabel = isInvoiceReq
              ? (r.status === 'draft' ? 'Tax Invoice Draft' : (STATUS_LABELS[r.status] ?? r.status.replace(/_/g, ' ')))
              : (STATUS_LABELS[r.status] ?? r.status.replace(/_/g, ' '));
            const statusTone = STATUS_TONE[r.status] ?? 'bg-slate-100 text-slate-800';
            const waitingOn: WaitingOn = r.waitingOn ?? 'none';
            const preparedBy = preparedByOf(r);
            const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
            const quotDoc = (r as any)._quotDoc || (r.requestType === 'quotation' ? r : null);
            const ms = (Array.isArray(r.milestones) && r.milestones.length > 0)
              ? r.milestones
              : (Array.isArray((r as any).template_data?.milestones) && (r as any).template_data.milestones.length > 0)
                ? (r as any).template_data.milestones
                : (Array.isArray(quotDoc?.milestones) && quotDoc.milestones.length > 0)
                  ? quotDoc.milestones
                  : (Array.isArray(quotDoc?.paymentSchedule?.milestones) && quotDoc.paymentSchedule.milestones.length > 0)
                    ? quotDoc.paymentSchedule.milestones
                    : (Array.isArray(quotDoc?.template_data?.milestones) && quotDoc.template_data.milestones.length > 0)
                      ? quotDoc.template_data.milestones
                      : (Array.isArray(r.paymentSchedule?.milestones) && r.paymentSchedule.milestones.length > 0)
                        ? r.paymentSchedule.milestones
                        : [];

            const pType = quotDoc?.paymentType || (quotDoc as any)?.template_data?.paymentType || (r as any).paymentType || (r as any).template_data?.paymentType;
            const rawTerms = quotDoc?.paymentTerms || (quotDoc as any)?.template_data?.paymentTerms || (r as any).paymentTerms || (r as any).template_data?.paymentTerms || '';
            const isExplicitFullPayment = pType === 'full_payment' || (!pType && ms.length <= 1);
            const isMilestoneQuotation = pType === 'milestone_based' || (!isExplicitFullPayment && ms.length > 1) || (ms.length > 0 && pType !== 'full_payment');

            const cleanStr = (str?: string | null) => {
              if (!str) return '';
              let s = str.trim();
              s = s.replace(/\s*\(\s*\(([^)]+)\)\s*\)/g, '($1)');
              s = s.replace(/\s*\((?:PI|TI)?\s*\([IVXLCDM]+\)\)/gi, '');
              s = s.replace(/\s*\((?:PI|TI)\)/gi, '');
              const match = s.match(/^(.*?)(?:\s*\(([IVXLCDM]+)\))+$/i);
              if (match) return `${match[1].trim()}(${match[2].toUpperCase()})`;
              return s.trim();
            };

            const baseQt = r.sourceQuotationNumber || ((r as any)._quotDoc?.number) || (r.requestType === 'quotation' ? r.number : '') || r.number;
            const baseQtId = r.sourceQuotationId || ((r as any)._quotDoc?.id) || ((r as any)._quotDoc?._id) || r.id;

            // A proforma billed stage by stage lists none of its running bills; only the final tax invoice
            // (raised once every stage is paid) can show here.
            const stageBilled = Boolean((r as any).isStageBilled) || ((r as any).paymentType === 'milestone_based' && ms.length > 1);

            // Detect any tax invoice generated for this card or any of its milestones
            const isThisInvoice = !isRunningBillRef(r) && (r.requestType === 'invoice' || (Boolean(r.number) && /^TI/i.test(r.number)) || Boolean(r.invoiceNumber && !r.invoiceNumber.startsWith('PI') && !r.invoiceNumber.startsWith('QT')));
            const linkedInv = isRunningBillRef((r as any)._invoiceDoc) ? null : (r as any)._invoiceDoc;
            const matchedInvFromList = rawRequests.find((x: any) =>
              !isRunningBillRef(x) &&
              (x.requestType === 'invoice' || (Boolean(x.number) && /^TI/i.test(x.number)) || Boolean(x.invoiceNumber)) &&
              (
                (baseQt && (x.sourceQuotationNumber === baseQt || x.number === baseQt)) ||
                (baseQtId && (String(x.sourceQuotationId) === String(baseQtId) || String(x._id || x.id) === String(baseQtId))) ||
                (r.sourceQuotationNumber && x.sourceQuotationNumber === r.sourceQuotationNumber)
              )
            );

            // Find any milestone that has an invoice
            const milestoneWithInvoiceIdx = stageBilled ? -1 : ms.findIndex((m: any, mIdx: number) => {
              const localDoc = generatedInvoices[`inv-${r.id}-${mIdx}`] || (baseQtId && generatedInvoices[`inv-${baseQtId}-${mIdx}`]);
              return Boolean(m.invoiceNumber) || Boolean(m.invoiceRequestId) || Boolean(localDoc) ||
                ['invoice_generated', 'paid', 'partially_paid', 'sent', 'approved'].includes(m.status) ||
                ['invoice_generated', 'paid', 'partially_paid', 'sent', 'approved'].includes(m.invoiceStatus);
            });
            const milestoneWithInvoice = milestoneWithInvoiceIdx >= 0 ? ms[milestoneWithInvoiceIdx] : null;

            const anyLocalGenDoc = stageBilled ? undefined : Object.entries(generatedInvoices).find(([k]) =>
              k.startsWith(`inv-${r.id}-`) || (baseQtId && k.startsWith(`inv-${baseQtId}-`))
            )?.[1];

            const cardInvoiceDoc = (isThisInvoice ? r : null) || linkedInv || matchedInvFromList || anyLocalGenDoc ||
              (milestoneWithInvoice?.invoiceRequestId ? rawRequests.find((x: any) => String(x.id || x._id) === String(milestoneWithInvoice.invoiceRequestId)) : null) ||
              r;

            const rawCardInvoiceNumber = (isThisInvoice ? (r.invoiceNumber || r.number) : '')
              || linkedInv?.invoiceNumber || linkedInv?.number
              || matchedInvFromList?.invoiceNumber || matchedInvFromList?.number
              || milestoneWithInvoice?.invoiceNumber
              || anyLocalGenDoc?.invoiceNumber || anyLocalGenDoc?.number
              || (r.status === 'sent' && r.number?.startsWith('TI') ? r.number : '');
            const cardInvoiceNumber = cleanStr(rawCardInvoiceNumber);

            const hasCardInvoice = !isRunningBillRef(cardInvoiceNumber) && !isRunningBillRef(cardInvoiceDoc) && (Boolean(cardInvoiceNumber) || isThisInvoice || Boolean(linkedInv) || Boolean(matchedInvFromList) || (milestoneWithInvoiceIdx >= 0) || Boolean(anyLocalGenDoc));
            const cardInvoiceMilestoneIdx = milestoneWithInvoiceIdx >= 0 ? milestoneWithInvoiceIdx : (r.milestoneIndex !== undefined && r.milestoneIndex !== null ? Number(r.milestoneIndex) : undefined);

            return (
              <div
                key={r.id}
                className={cn(cardClassName(), 'overflow-hidden transition-shadow')}
              >
                <div className="flex flex-wrap items-center gap-3 p-4">
                  <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(() => {
                        const qtNum  = r.sourceQuotationNumber || ((r as any)._quotDoc?.number) || (r.requestType === 'quotation' ? r.number : null);

                        let rawPiStr = r.proformaNumber || ((r as any)._proformaDoc?.proformaNumber) || ((r as any)._proformaDoc?.number) || (r.requestType === 'proforma' ? (r.proformaNumber || r.number) : null);
                        if (rawPiStr) {
                          rawPiStr = rawPiStr.replace(/\([IVXLCDM\d]+\)$/i, '');
                        }

                        const isThisInvoice = r.requestType === 'invoice';
                        const linkedInv = (r as any)._invoiceDoc;
                        const TI_APPROVED_STATUSES = ['approved', 'sent', 'paid', 'partially_paid', 'invoice_generated'];
                        const isThisApprovedInvoice = (isThisInvoice || (isExplicitFullPayment && Boolean(r.invoiceNumber))) && TI_APPROVED_STATUSES.includes(r.status);
                        const isApprovedLinkedInvoice = linkedInv && (linkedInv.requestType === 'invoice' || isExplicitFullPayment) && TI_APPROVED_STATUSES.includes(linkedInv.status);
                        let rawTiStr = isThisInvoice
                          ? (r.invoiceNumber || r.number)
                          : (isThisApprovedInvoice
                              ? (r.invoiceNumber || r.number)
                              : (isApprovedLinkedInvoice ? (linkedInv.invoiceNumber || linkedInv.number) : null));
                        if (rawTiStr) {
                          rawTiStr = rawTiStr.replace(/\([IVXLCDM\d]+\)$/i, '');
                        }
                        const piNum  = cleanStr(rawPiStr);
                        const tiNum  = cleanStr(rawTiStr);

                        // Unified clean request row: QT → PI → TI
                        return (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {/* QT */}
                            {qtNum && (
                              <>
                                <span className="text-xs font-mono font-semibold text-slate-500">{qtNum}</span>
                                {(piNum || tiNum) && <span className="text-slate-400 text-xs">→</span>}
                              </>
                            )}
                            {/* PI */}
                            {piNum && (
                              <>
                                <span className="text-sm font-bold font-mono text-indigo-600 dark:text-indigo-400">{piNum}</span>
                                {tiNum && <span className="text-slate-400 text-xs">→</span>}
                              </>
                            )}
                            {/* TI */}
                            {tiNum && (
                              <span className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">{tiNum}</span>
                            )}
                            {/* Standalone QT */}
                            {!qtNum && !piNum && !tiNum && (
                              <span className="font-semibold text-sm font-mono text-primary">{r.number}</span>
                            )}
                          </div>
                        );
                      })()}
                      {r.currentRevisionLabel && (r.resubmissionCount || 0) > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                          {r.currentRevisionLabel}
                        </span>
                      )}
                      {['rejected', 'revision_needed', 'proforma_rejected'].includes(r.status) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
                          R{(r.resubmissionCount || 0) + 1}
                        </span>
                      )}
                      {(r.resubmissionCount || 0) > 0 && !['rejected', 'revision_needed', 'proforma_rejected'].includes(r.status) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                          V{r.resubmissionCount}
                        </span>
                      )}
                      <Badge className={`${statusTone} border text-xs font-semibold px-2.5 py-0.5`}>
                        {statusLabel}
                      </Badge>
                      {waitingOn !== 'none' && (
                        <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                          Waiting on {APPROVER_LABELS[waitingOn]}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {r.clientName || 'No client'}
                      {r.sourceQuotationNumber && r.sourceQuotationNumber !== r.number && !r.number.startsWith('QT') ? ` · from ${r.sourceQuotationNumber}` : ''}
                      {(r.resubmissionCount || 0) > 0 ? ` · Resubmitted: ${r.resubmissionCount} time${(r.resubmissionCount || 0) > 1 ? 's' : ''}` : ''}
                      {preparedBy ? ` · prepared by ${preparedBy}` : ''}
                      {(r.requestedByName || (r as any).createdByName) ? ` · raised by ${r.requestedByName || (r as any).createdByName}` : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="font-bold text-sm font-mono">{formatINR(r.total)}</div>
                      <div className="text-[11px] text-muted-foreground">
                        net {formatINR(r.subtotal)} + tax {formatINR(r.taxTotal)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Milestone Stages Breakdown — each stage has its breadcrumb + buttons */}
                {isMilestoneQuotation && (
                  <div className="px-4 pb-4 space-y-2.5">
                    {ms.map((m: any, idx: number) => {
                      const baseQt = r.sourceQuotationNumber || ((r as any)._quotDoc?.number) || (r.requestType === 'quotation' ? r.number : '') || r.number;
                      const baseQtId = r.sourceQuotationId || ((r as any)._quotDoc?.id) || ((r as any)._quotDoc?._id) || r.id;

                      const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
                      const expectedRoman = ROMANS[idx] || String(idx + 1);

                      // Helper to check if a document strictly matches this stage index
                      const matchesStageIndex = (doc: any) => {
                        if (!doc) return false;
                        if (doc.milestoneIndex !== undefined && doc.milestoneIndex !== null && Number(doc.milestoneIndex) === idx) return true;
                        if (doc.template_data?.milestoneIndex !== undefined && doc.template_data?.milestoneIndex !== null && Number(doc.template_data.milestoneIndex) === idx) return true;
                        if (doc.milestonePhaseLabel && doc.milestonePhaseLabel.replace(/[()]/g, '').trim().toUpperCase() === expectedRoman) return true;
                        const num = doc.proformaNumber || doc.number || '';
                        if (num.toUpperCase().includes(`(${expectedRoman})`)) return true;
                        return false;
                      };

                      // If m.proformaNumber has a mismatched roman numeral from another stage (e.g. (II) when idx=0), ignore it for this stage
                      const mNumHasOtherRoman = m.proformaNumber && /\(([IVXLCDM]+|\d+)\)/i.test(m.proformaNumber) && !m.proformaNumber.toUpperCase().includes(`(${expectedRoman})`);
                      const validMProformaNumber = (!mNumHasOtherRoman && m.proformaNumber) ? m.proformaNumber : undefined;

                      // Find the proforma document that belongs EXACTLY to this milestone stage.
                      const phaseProformaDoc = (m._proformaDoc && matchesStageIndex(m._proformaDoc) ? m._proformaDoc : undefined)
                        || (m.proformaRequestId
                            ? rawRequests.find(x => (String(x.id) === String(m.proformaRequestId) || String((x as any)._id) === String(m.proformaRequestId)) && matchesStageIndex(x))
                            : undefined)
                        || (validMProformaNumber
                            ? rawRequests.find(x => (x.proformaNumber === validMProformaNumber || x.number === validMProformaNumber) &&
                                (x.requestType === 'proforma' || x.number?.startsWith('PI')))
                            : undefined)
                        || rawRequests.find(x =>
                            (x.requestType === 'proforma' || x.number?.startsWith('PI')) &&
                            (
                              (x.sourceQuotationNumber && (x.sourceQuotationNumber === baseQt || x.sourceQuotationNumber === r.number)) ||
                              (x.sourceQuotationId && (String(x.sourceQuotationId) === String(baseQtId) || String(x.sourceQuotationId) === String(r.id)))
                            ) &&
                            matchesStageIndex(x)
                          ) || null;

                      const rawPhasePi = phaseProformaDoc?.proformaNumber || phaseProformaDoc?.number || validMProformaNumber || '';
                      let mPi = cleanStr(rawPhasePi).replace(/\([IVXLCDM\d]+\)$/i, '');

                      // Helper to check if a Tax Invoice document strictly matches this stage index
                      const matchesInvoiceStageIndex = (doc: any) => {
                        if (!doc) return false;
                        if (doc.milestoneIndex !== undefined && doc.milestoneIndex !== null && Number(doc.milestoneIndex) === idx) return true;
                        if (doc.template_data?.milestoneIndex !== undefined && doc.template_data?.milestoneIndex !== null && Number(doc.template_data.milestoneIndex) === idx) return true;
                        if (doc.milestonePhaseLabel && doc.milestonePhaseLabel.replace(/[()]/g, '').trim().toUpperCase() === expectedRoman) return true;
                        const num = doc.invoiceNumber || doc.number || '';
                        if (num.toUpperCase().includes(`(${expectedRoman})`)) return true;
                        return false;
                      };

                      // If m.invoiceNumber has a mismatched roman numeral from another stage (e.g. (II) when idx=0), ignore it for this stage
                      const mNumHasOtherInvoiceRoman = m.invoiceNumber && /\(([IVXLCDM]+|\d+)\)/i.test(m.invoiceNumber) && !m.invoiceNumber.toUpperCase().includes(`(${expectedRoman})`);
                      const validMInvoiceNumber = (!mNumHasOtherInvoiceRoman && m.invoiceNumber) ? m.invoiceNumber : undefined;

                      const phaseInvoiceDoc = m._invoiceDoc
                        || (m.invoiceRequestId
                            ? rawRequests.find(x => (String(x.id) === String(m.invoiceRequestId) || String((x as any)._id) === String(m.invoiceRequestId)))
                            : undefined)
                        || (validMInvoiceNumber
                            ? rawRequests.find(x => (x.invoiceNumber === validMInvoiceNumber || x.number === validMInvoiceNumber) &&
                                (x.requestType === 'invoice' || x.number?.startsWith('TI') || x.number?.startsWith('INV')))
                            : undefined)
                        || rawRequests.find(x =>
                            (x.requestType === 'invoice' || x.number?.startsWith('TI') || x.number?.startsWith('INV')) &&
                            (
                              (x.sourceQuotationNumber && (x.sourceQuotationNumber === baseQt || x.sourceQuotationNumber === r.number)) ||
                              (x.sourceQuotationId && (String(x.sourceQuotationId) === String(baseQtId) || String(x.sourceQuotationId) === String(r.id)))
                            ) &&
                            matchesInvoiceStageIndex(x)
                          ) || null;

                      const rawPhaseTi = phaseInvoiceDoc?.invoiceNumber || phaseInvoiceDoc?.number || validMInvoiceNumber || m.invoiceNumber || '';
                      let mTi = cleanStr(rawPhaseTi).replace(/\([IVXLCDM\d]+\)$/i, '');

                      // Also check locally-stored generated invoice (appears immediately after creation)
                      const localGenDoc = generatedInvoices[`inv-${r.id}-${idx}`] || null;
                      const localGenTi = localGenDoc?.invoiceNumber || localGenDoc?.number || null;
                      const isInvoiceCreated = Boolean(mTi) || Boolean(phaseInvoiceDoc) || Boolean(validMInvoiceNumber) || Boolean(m.invoiceNumber) || Boolean(m.invoiceRequestId) || Boolean(localGenDoc) || ['invoice_generated', 'paid', 'partially_paid'].includes(m.status) || ['invoice_generated', 'paid', 'partially_paid'].includes(m.invoiceStatus);
                      const isGenerated = isInvoiceCreated;
                      const isPhaseProformaApproved = m.status === 'proforma_approved' || m.proformaStatus === 'proforma_approved' || phaseProformaDoc?.status === 'proforma_approved';
                      const isPhaseDraft = (phaseInvoiceDoc && ['draft', 'invoice_draft'].includes(phaseInvoiceDoc.status)) ||
                                           (phaseProformaDoc && ['draft', 'proforma_draft'].includes(phaseProformaDoc.status)) ||
                                           (idx === 0 && r.status === 'draft');
                      const targetPhaseDoc = phaseInvoiceDoc || phaseProformaDoc || r;
                      const isPhaseExpanded = expanded === `${r.id}_${idx}`;

                      let stageStatusLabel = 'Pending';
                      let stageStatusTone = 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
                      if (mTi && phaseInvoiceDoc?.status === 'draft') {
                        stageStatusLabel = 'Tax Invoice Draft';
                        stageStatusTone = 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-200';
                      } else if (isGenerated) {
                        stageStatusLabel = 'Invoiced';
                        stageStatusTone = 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200';
                      } else if (isPhaseProformaApproved) {
                        stageStatusLabel = 'Proforma Ready';
                        stageStatusTone = 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-950 dark:text-indigo-200';
                      } else if (mPi) {
                        stageStatusLabel = 'Proforma Pending';
                        stageStatusTone = 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-200';
                      }

                      return (
                        <div key={idx} className="border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                {m.name || `Stage ${idx + 1}`}
                              </span>
                              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                ({m.percentage}%)
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded font-bold border ${stageStatusTone}`}>
                                {stageStatusLabel}
                              </span>
                            </div>

                            <div className="text-right font-mono text-xs font-bold text-slate-700 dark:text-slate-300">
                              {formatINR(m.milestoneTotal || m.amountDue || (r.total * ((m.percentage || 25) / 100)))}
                            </div>
                          </div>

                          {/* Action Buttons for this specific milestone stage */}
                          <div className="flex items-center gap-1.5 flex-wrap pt-1">
                            {/* View Quotation */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs text-primary border-primary/30 hover:bg-primary/5"
                              onClick={() => handleOpenDrawer(r, 'quotation', idx)}
                            >
                              <Eye className="h-3 w-3 mr-1" /> View Quotation
                            </Button>

                            {/* View Proforma Invoice */}
                            {(Boolean(mPi) || Boolean(phaseProformaDoc) || Boolean(m.proformaNumber) || Boolean(m.proformaRequestId) || isPhaseProformaApproved) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-indigo-600 border-indigo-300 hover:bg-indigo-50 font-semibold"
                                onClick={() => handleOpenDrawer(phaseProformaDoc || r, 'proforma', idx, mPi || phaseProformaDoc?.proformaNumber || phaseProformaDoc?.number)}
                              >
                                <FileText className="h-3 w-3 mr-1" /> View Proforma Invoice
                              </Button>
                            )}

                            {/* Request Proforma (when no proforma yet for this stage) — calls request-proforma directly, one per stage */}
                            {(!mPi && !phaseProformaDoc && !m.proformaNumber && !isPhaseProformaApproved && isQuotationUsable(r)) && (
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={busy}
                                onClick={async () => {
                                  const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
                                  const phaseLabel = ROMANS[idx] || String(idx + 1);
                                  const qDoc = (r as any)._quotDoc || (r.requestType === 'quotation' ? r : null);
                                  const qId = qDoc?._id || qDoc?.id || r.sourceQuotationId || r.id;
                                  const qNo = qDoc?.number || r.sourceQuotationNumber || r.number || '';
                                  let dealData = (r as any).fromDeal || (qDoc as any)?.fromDeal || null;
                                  const dealId = (r as any).sourceDealId || (r as any).dealId || (qDoc as any)?.sourceDealId || (qDoc as any)?.dealId || (r as any).clientId;
                                  if (!dealData && dealId) {
                                    try {
                                      const res = await api.get(`/api/deals/${dealId}`);
                                      dealData = res.data?.deal || res.data?.data || res.data;
                                    } catch {}
                                  }
                                  if (!dealData) {
                                    dealData = {
                                      clientName: r.clientName || qDoc?.clientName || '',
                                      clientAddress: (r as any).clientAddress || (qDoc as any)?.clientAddress || '',
                                      clientGstin: (r as any).clientGstin || (qDoc as any)?.clientGstin || '',
                                      contactPerson: (r as any).contactPerson || (qDoc as any)?.contactPerson || '',
                                      email: (r as any).clientEmail || (qDoc as any)?.clientEmail || '',
                                      phone: (r as any).clientPhone || (qDoc as any)?.clientPhone || '',
                                      services: (r as any).services || (qDoc as any)?.services || [],
                                      value: r.total || qDoc?.total || 0,
                                      notes: r.notes || qDoc?.notes || '',
                                    };
                                  }
                                  if (isSalesOnly) {
                                    try {
                                      await api.post(`/invoice-requests/${qId}/request-proforma`, {
                                        milestoneIndex: idx,
                                        milestonePhaseLabel: `(${phaseLabel})`,
                                        note: `Milestone Proforma Phase ${phaseLabel} requested by ${currentUser?.name || 'Sales'}`,
                                      });
                                      toast.success(`Proforma for Milestone ${phaseLabel} requested. Notification sent to Superadmin.`);
                                      refetch();
                                      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
                                      queryClient.invalidateQueries({ queryKey: ['quotations'] });
                                      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
                                    } catch (err: any) {
                                      toast.error(getApiErrorMessage(err, 'request proforma for this milestone'));
                                    }
                                    return;
                                  }
                                  navigate(`/app/sales/proforma/create?quotationId=${encodeURIComponent(qId)}&quotationNumber=${encodeURIComponent(qNo)}&dealId=${encodeURIComponent(dealId || '')}&milestoneIndex=${idx}&milestonePhaseLabel=${encodeURIComponent(`(${phaseLabel})`)}`);
                                }}
                                className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Request Proforma
                              </Button>
                            )}

                            {/* Submit Draft (when proforma is draft) */}
                            {phaseProformaDoc && ['draft', 'proforma_draft'].includes(phaseProformaDoc.status) && (
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => run(() => actions.submit.mutateAsync({ id: phaseProformaDoc.id || (phaseProformaDoc as any)._id }), 'Proforma submitted.')}
                                className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              >
                                <Send className="h-3 w-3 mr-1" /> Submit Draft
                              </Button>
                            )}

                            {/* Edit Proforma (when proforma is draft or rejected or requested) */}
                            {phaseProformaDoc && ['draft', 'proforma_draft', 'proforma_requested', 'proforma_rejected', 'revision_needed'].includes(phaseProformaDoc.status) && (
                              <Button
                                variant="secondary"
                                size="sm"
                                className="h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50 font-semibold"
                                onClick={() => navigate(salesProformaEditPath(phaseProformaDoc.id || (phaseProformaDoc as any)._id))}
                              >
                                <Edit className="h-3 w-3 mr-1" /> Edit Proforma
                              </Button>
                            )}

                            {/* Resubmit Proforma (when proforma is rejected) */}
                            {phaseProformaDoc?.status === 'proforma_rejected' && (
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => setProformaModalState({
                                  open: true,
                                  editingDraft: phaseProformaDoc,
                                  isResubmission: true,
                                  documentId: phaseProformaDoc.id || (phaseProformaDoc as any)._id,
                                  rejectionReason: phaseProformaDoc.reviewNote || (phaseProformaDoc as any).rejectionReason,
                                  proformaNumber: phaseProformaDoc.proformaNumber || phaseProformaDoc.number,
                                  quotationNumber: baseQt,
                                })}
                                className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" /> Resubmit Proforma
                              </Button>
                            )}


                            {/* Details */}
                            <Button
                              variant="secondary"
                              size="sm"
                              className="h-7 text-xs font-medium"
                              onClick={() => setExpanded(isPhaseExpanded ? null : `${r.id}_${idx}`)}
                            >
                              {isPhaseExpanded ? 'Hide' : 'Details'}
                            </Button>

                            {/* Submit (if draft invoice or draft proforma) */}
                            {isPhaseDraft && (
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => run(() => actions.submit.mutateAsync({ id: targetPhaseDoc.id }), 'Submitted for Superadmin approval.')}
                                className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                              >
                                <Send className="h-3 w-3 mr-1" /> Submit
                              </Button>
                            )}

                            {/* Delete Draft (if draft invoice or draft proforma) */}
                            {isPhaseDraft && (
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={busy}
                                onClick={() => run(() => actions.deleteDraft.mutateAsync({ id: targetPhaseDoc.id }), 'Draft deleted.')}
                                className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                              >
                                <Trash2 className="h-3 w-3 mr-1" /> Delete Draft
                              </Button>
                            )}

                            {/* Link to Payment Schedule Section */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium ml-auto"
                              onClick={() => navigate(`/app/sales/milestones?quotationNumber=${encodeURIComponent(baseQt)}&from=${encodeURIComponent('/invoice-requests')}`, { state: { from: '/invoice-requests' } })}
                            >
                              <Layers className="h-3 w-3 mr-1 text-indigo-500" /> Milestone Schedule
                            </Button>
                          </div>

                          {/* Expanded Details for this stage */}
                          {isPhaseExpanded && (
                            <div className="mt-2 p-3 bg-white dark:bg-slate-950 rounded border border-slate-200 dark:border-slate-800 text-xs space-y-3 shadow-inner">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                                <div><span className="text-slate-400">Milestone:</span> <span className="font-semibold">{m.name || `Stage ${idx + 1}`}</span></div>
                                <div><span className="text-slate-400">Percentage:</span> <span className="font-semibold">{m.percentage}%</span></div>
                                <div><span className="text-slate-400">Amount Due:</span> <span className="font-semibold font-mono text-slate-800 dark:text-slate-200">{formatINR(m.milestoneTotal || m.amountDue || 0)}</span></div>
                                <div><span className="text-slate-400">Stage Status:</span> <span className="font-semibold capitalize">{m.status?.replace(/_/g, ' ') || 'Pending'}</span></div>
                              </div>

                              {/* Proforma & Tax Invoice Status Tracking */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                                {/* Proforma Status Tracker */}
                                <div className="p-2.5 rounded bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                      <FileText className="w-3.5 h-3.5 text-indigo-500" /> Proforma Status
                                    </span>
                                    {isPhaseProformaApproved ? (
                                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                        Ready for Invoice
                                      </span>
                                    ) : mPi || phaseProformaDoc ? (
                                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                                        Created
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-300">
                                        Not Requested
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-slate-600 dark:text-slate-400 space-y-0.5">
                                    <div><span className="text-slate-400">Proforma No:</span> <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{mPi || '—'}</span></div>
                                    <div><span className="text-slate-400">Document State:</span> <span className="font-medium">{isPhaseProformaApproved ? 'Ready for Invoice' : mPi || phaseProformaDoc ? 'Created' : 'Not Requested'}</span></div>
                                  </div>
                                </div>

                                {/* Tax Invoice Status Tracker */}
                                <div className="p-2.5 rounded bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                      <Receipt className="w-3.5 h-3.5 text-emerald-500" /> Tax Invoice Status
                                    </span>
                                    {isInvoiceCreated ? (
                                      (['approved', 'paid', 'invoice_generated'].includes(phaseInvoiceDoc?.status) || ['paid', 'invoice_generated'].includes(m.status)) ? (
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                          Approved / Invoiced
                                        </span>
                                      ) : (
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-300">
                                          Created / Generated
                                        </span>
                                      )
                                    ) : (
                                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-300">
                                        Pending Creation
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-slate-600 dark:text-slate-400 space-y-0.5">
                                    <div><span className="text-slate-400">Invoice No:</span> <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{mTi || '—'}</span></div>
                                    <div><span className="text-slate-400">Invoice State:</span> <span className="font-medium capitalize">{isInvoiceCreated ? (m.invoiceStatus || phaseInvoiceDoc?.status?.replace(/_/g, ' ') || 'Created') : 'Pending Creation'}</span></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Actions */}
                <div className="px-4 pb-4 flex items-center gap-2 flex-wrap">
                  {/* View Tax Invoice — for single/full payment quotation or specific phase cards */}
                  {!isMilestoneQuotation && hasCardInvoice && (
                    <Button
                      variant="outline"
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                      onClick={() => handleOpenDrawer(cardInvoiceDoc, 'invoice', cardInvoiceMilestoneIdx, cardInvoiceNumber || undefined)}
                    >
                      <Receipt className="h-3.5 w-3.5 mr-1.5" /> View Tax Invoice {cardInvoiceNumber ? `(${cardInvoiceNumber})` : ''}
                    </Button>
                  )}

                  {/* View Quotation — ONLY if doc is a quotation or has a separate linked quotation */}
                  {(r.requestType === 'quotation' || Boolean((r as any)._quotDoc) || (Boolean(r.sourceQuotationNumber) && r.sourceQuotationNumber !== r.number)) && (
                    <Button variant="outline" className="text-slate-700 border-slate-300 hover:bg-slate-50 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-800 font-semibold" onClick={() => handleOpenDrawer(r, 'quotation')}>
                      <Eye className="h-3.5 w-3.5 mr-1.5" /> View Quotation
                    </Button>
                  )}

                  {/* View Proforma */}
                  {(hasRealProformaNumber(r) || Boolean(r.proformaId) || Boolean((r as any)._proformaDoc) || r.requestType === 'proforma' || r.status.startsWith('proforma') || r.proformaFullyApproved) && (
                    <Button variant="outline" className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 font-semibold" onClick={() => handleOpenDrawer(r, 'proforma')}>
                      <FileText className="h-3.5 w-3.5 mr-1.5" /> View Proforma Invoice
                    </Button>
                  )}

                  {/* Send by Email - real SMTP send from the sender's own
                      mailbox (apps/api/.../invoice-request-email.ts), gated
                      server-side the same way the modal explains client-side
                      (Tax Invoices need invoiceApprovedBy set first). */}
                  {/* A proforma goes to a client only once the superadmin approved it. */}
                  {(r.requestType !== 'proforma' || isProformaApproved(r)) && (
                    <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => setSendEmailDoc(r)}>
                      <Send className="h-3.5 w-3.5 mr-1.5" /> Send Email
                    </Button>
                  )}

                  <Button variant="secondary" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                    {expanded === r.id ? 'Hide' : 'Details'}
                  </Button>

                  {/* Non-milestone workflow actions */}
                  {!isMilestoneQuotation && (
                    <>
                      {/* Edit Proforma — opens the full-page proforma form (same as create) in edit mode,
                          which fetches the document fresh from the API */}
                      {!Boolean(r.isConfirmed || (r as any).isLocked || r.status === 'confirmed') && r.requestType === 'proforma' && ['draft', 'proforma_draft', 'proforma_requested', 'proforma_rejected', 'revision_needed'].includes(r.status) && (
                        <Button
                          variant="secondary"
                          className="text-indigo-700 border-indigo-300 hover:bg-indigo-50 text-xs font-semibold"
                          onClick={() => navigate(salesProformaEditPath(r.id || (r as any)._id))}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1" /> Edit Proforma
                        </Button>
                      )}

                      {!Boolean(r.isConfirmed || (r as any).isLocked || r.status === 'confirmed') && (r.canSubmit || r.status === 'draft') && (
                        <Button
                          disabled={busy}
                          onClick={() => run(() => actions.submit.mutateAsync({ id: r.id }), r.requestType === 'proforma' ? 'Proforma submitted.' : 'Submitted for approval.')}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                        >
                          <Send className="h-3.5 w-3.5 mr-1" /> {r.requestType === 'proforma' ? 'Submit Proforma' : 'Submit'}
                        </Button>
                      )}

                      {/* The superadmin approves a submitted proforma, or returns it with a reason. */}
                      {r.canApproveNow && r.requestType === 'proforma' && (
                        <Button
                          disabled={busy}
                          onClick={() => run(
                            () => actions.approve.mutateAsync({ id: r.id, stage: 'proforma' }),
                            'Proforma approved.',
                          )}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          Approve Proforma
                        </Button>
                      )}

                      {/* Either approver may reject at any point in the sequence. */}
                      {r.canRejectNow && stage && (
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => { setRejecting({ id: r.id, stage }); setRejectReason(''); }}
                          className="text-rose-700 border-rose-300 hover:bg-rose-50 text-xs font-semibold"
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      )}


                      {/* Resubmit Proforma — when proforma is proforma_rejected */}
                      {(() => {
                        const proformaDoc = (r as any)._proformaDoc || (r.requestType === 'proforma' ? r : null);
                        const isProformaRejected = r.status === 'proforma_rejected' || proformaDoc?.status === 'proforma_rejected';
                        const targetProformaId = proformaDoc?.id || r.id;
                        if (!r.canResubmitProforma && !isProformaRejected) return null;
                        return (
                          <Button
                            disabled={busy}
                            onClick={() => run(() => actions.resubmitProforma.mutateAsync({ id: targetProformaId }), 'Proforma resubmitted for Superadmin approval.')}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                          >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resubmit Proforma
                          </Button>
                        );
                      })()}
                    </>
                  )}

                  {r.canDelete && (
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => run(() => actions.deleteDraft.mutateAsync({ id: r.id }), 'Draft deleted.')}
                      className="text-rose-700 border-rose-300 hover:bg-rose-50 text-xs"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Draft
                    </Button>
                  )}

                  {r.status === 'sent' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded border border-slate-200">
                        <Lock className="w-3 h-3 text-slate-400" /> Closed — invoice issued
                      </span>
                    </div>
                  )}
                </div>

                {/* Rejection reason — mandatory, so it is collected before the call. */}
                {rejecting?.id === r.id && (
                  <div className="border-t border-border bg-rose-50/60 dark:bg-rose-950/20 px-4 py-3 space-y-2">
                    <label htmlFor={`reject-reason-${r.id}`} className="text-xs font-semibold text-rose-900 dark:text-rose-200">
                      Reason for rejecting this {rejecting.stage === 'proforma' ? 'Proforma' : 'Quotation'} (required)
                    </label>
                    <textarea
                      id={`reject-reason-${r.id}`}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      className="w-full text-xs rounded border border-rose-300 bg-card px-2 py-1.5"
                      placeholder="Explain what needs to change. Sales sees this."
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="secondary" onClick={() => { setRejecting(null); setRejectReason(''); }}>
                        Cancel
                      </Button>
                      <Button
                        disabled={busy || !rejectReason.trim()}
                        onClick={submitRejection}
                        className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
                      >
                        Confirm Rejection
                      </Button>
                    </div>
                  </div>
                )}

                {r.reviewNote && ['revision_needed', 'rejected', 'proforma_rejected'].includes(r.status) && (
                  <div className="px-4 pb-3">
                    <p className="text-xs bg-orange-50 border border-orange-200 text-orange-900 rounded px-3 py-2">
                      <strong>Reviewer note:</strong> {r.reviewNote}
                    </p>
                  </div>
                )}

                {expanded === r.id && (
                  <div className="border-t border-border bg-slate-50/60 p-4 space-y-4">
                    {/* Document Lifecycle & Status Tracking */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Quotation Status */}
                      <div className={cn(cardClassName({ variant: 'inset' }), 'p-3 space-y-1')}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Quotation Status</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            r.quotationFullyApproved || r.status === 'approved' || (r as any)._quotDoc?.status === 'approved'
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : 'bg-slate-100 text-slate-700 border border-slate-300'
                          }`}>
                            {r.quotationFullyApproved || r.status === 'approved' || (r as any)._quotDoc?.status === 'approved' ? 'Created' : (r.status?.replace(/_/g, ' ') || 'Pending')}
                          </span>
                        </div>
                        <div className="text-xs font-mono font-bold text-slate-800 dark:text-slate-200">
                          {(r as any)._quotDoc?.number || (r.number?.startsWith('QT') ? r.number : r.sourceQuotationNumber) || '—'}
                        </div>
                      </div>

                      {/* Proforma Status */}
                      <div className={cn(cardClassName({ variant: 'inset' }), 'p-3 space-y-1')}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" /> Proforma Status
                          </span>
                          {(() => {
                            const pDoc = (r as any)._proformaDoc || (r.requestType === 'proforma' ? r : null);
                            const isApproved = r.proformaFullyApproved || r.status === 'proforma_approved' || pDoc?.status === 'proforma_approved';
                            const isPending = ['proforma_requested', 'proforma_under_review'].includes(r.status) || ['proforma_requested', 'proforma_under_review'].includes(pDoc?.status);
                            const isRejected = r.status === 'proforma_rejected' || pDoc?.status === 'proforma_rejected';
                            if (isApproved) {
                              return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">Ready for Invoice</span>;
                            }
                            if (isPending) {
                              return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">Created</span>;
                            }
                            if (isRejected) {
                              return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-300">Rejected</span>;
                            }
                            const hasAnyProforma = Boolean(r.proformaNumber || pDoc?.number || (r.milestones && r.milestones.some((m: any) => m.proformaNumber)));
                            return <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-300">{hasAnyProforma ? 'Created' : 'Not Requested'}</span>;
                          })()}
                        </div>
                        <div className="text-xs font-mono font-bold text-indigo-700 dark:text-indigo-300">
                          {r.proformaNumber || (r as any)._proformaDoc?.number || (r.requestType === 'proforma' ? r.number : '—')}
                        </div>
                      </div>

                      {/* Tax Invoice Status */}
                      <div className={cn(cardClassName({ variant: 'inset' }), 'p-3 space-y-1')}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <Receipt className="w-3.5 h-3.5" /> Tax Invoice Status
                          </span>
                          {(() => {
                            const iDoc = (r as any)._invoiceDoc || (r.requestType === 'invoice' ? r : null);
                            const isApproved = ['approved', 'sent', 'paid', 'invoice_generated'].includes(r.status) || ['approved', 'sent', 'paid', 'invoice_generated'].includes(iDoc?.status);
                            const isPending = ['invoice_pending_approval', 'requested'].includes(r.status) || ['invoice_pending_approval', 'requested'].includes(iDoc?.status);
                            const hasAnyInvoice = Boolean(r.invoiceNumber || iDoc?.number || (r.milestones && r.milestones.some((m: any) => m.invoiceNumber)));
                            if (isApproved) {
                              return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">Approved / Invoiced</span>;
                            }
                            if (isPending) {
                              return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">Pending Approval</span>;
                            }
                            if (hasAnyInvoice) {
                              return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-300">Created / Generated</span>;
                            }
                            return <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-300">Pending Creation</span>;
                          })()}
                        </div>
                        <div className="text-xs font-mono font-bold text-emerald-700 dark:text-emerald-300">
                          {r.invoiceNumber || (r as any)._invoiceDoc?.number || (r.requestType === 'invoice' ? r.number : '—')}
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b border-border font-bold">
                            <th className="py-1.5 pr-3">Item</th>
                            <th className="py-1.5 pr-3 text-right">Qty</th>
                            <th className="py-1.5 pr-3 text-right">Rate</th>
                            <th className="py-1.5 pr-3 text-right">Disc %</th>
                            <th className="py-1.5 pr-3 text-right">Tax %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.items.map((it, i) => (
                            <tr key={i} className="border-b border-border/40 last:border-0">
                              <td className="py-1.5 pr-3 font-medium">{it.name}</td>
                              <td className="py-1.5 pr-3 text-right">{it.quantity}</td>
                              <td className="py-1.5 pr-3 text-right font-mono">{formatINR(it.rate)}</td>
                              <td className="py-1.5 pr-3 text-right">{it.discountPercent || 0}</td>
                              <td className="py-1.5 pr-3 text-right">{it.taxRate || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {r.approvals && r.approvals.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground mb-1">Signatures</p>
                        <ul className="space-y-1">
                          {r.approvals.map((a, i) => (
                            <li key={i} className="text-[11px] text-muted-foreground">
                              {new Date(a.at).toLocaleString()} · {a.stage} · {APPROVER_LABELS[a.role === 'owner' ? 'superadmin' : a.role]} · {a.status}
                              {a.byName ? ` · ${a.byName}` : ''}
                              {a.note ? ` — ${a.note}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {r.history && r.history.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground mb-1">Audit History</p>
                        <ul className="space-y-1">
                          {r.history.map((h, i) => (
                            <li key={i} className="text-[11px] text-muted-foreground">
                              {new Date(h.at).toLocaleString()} · {h.byName || 'system'}
                              {h.from ? ` · ${h.from} → ${h.to}` : ` · ${h.to}`}
                              {h.note ? ` — ${h.note}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Slide-out Document Drawer */}
      <SalesDocumentWorkspaceDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        document={selectedDocData}
        readOnly
        onSaveHtml={handleSaveDrawerHtml}
        onApprove={handleDrawerApprove}
        onReject={handleDrawerReject}
        onRequestTaxInvoiceApproval={handleRequestTaxInvoiceApproval}
        onResubmit={handleDrawerResubmit}
        onOpenResubmissionForm={handleOpenResubmissionFromDrawer}
        onEditDraft={handleEditDraftFromDrawer}
        onSubmitDraft={handleDrawerSubmitDraft}
      />

      {proformaModalState?.open && (
        <ProformaInvoiceGenerator
          initialQuotationId={proformaModalState.quotationId}
          initialQuotationNumber={proformaModalState.quotationNumber}
          initialMilestoneIndex={proformaModalState.milestoneIndex}
          initialMilestonePhaseLabel={proformaModalState.milestonePhaseLabel}
          fromDeal={proformaModalState.fromDeal}
          fromLead={proformaModalState.fromLead}
          editingDraft={proformaModalState.editingDraft}
          isResubmission={proformaModalState.isResubmission}
          isRevision={proformaModalState.isRevision}
          currentRevisionLabel={proformaModalState.currentRevisionLabel}
          nextRevisionLabel={proformaModalState.nextRevisionLabel}
          documentId={proformaModalState.documentId}
          rejectionReason={proformaModalState.rejectionReason}
          proformaNumber={proformaModalState.proformaNumber}
          onClose={() => setProformaModalState(null)}
          onSuccess={() => {
            setProformaModalState(null);
            refetch();
          }}
        />
      )}

      {createProformaQuotId && (
        <ProformaInvoiceGenerator
          initialQuotationId={createProformaQuotId}
          onClose={() => setCreateProformaQuotId(null)}
          onSuccess={() => {
            setCreateProformaQuotId(null);
            refetch();
          }}
        />
      )}

      {sendEmailDoc && (
        <SendDocumentEmailModal
          open
          onClose={() => setSendEmailDoc(null)}
          documentId={sendEmailDoc.id}
          documentNumber={sendEmailDoc.number}
          requestType={sendEmailDoc.requestType || 'quotation'}
          clientEmail={sendEmailDoc.clientEmail}
          clientName={sendEmailDoc.clientName}
          invoiceApprovedBy={sendEmailDoc.invoiceApprovedBy}
        />
      )}
      </div>
    </div>
  );
}

export default InvoiceRequestsPage;
